import { withAvailabilityConfirmation } from './availability.ts';
import type { CustomerCallOptions, StartedCustomerCall } from '@shared/voice.ts';
import type {
  AccountNumber,
  SaveIvrMenuInput,
  SaveLocationVoiceInput,
  SaveVoiceTeamInput,
  UpdateVoiceNumberInput,
  VoiceCallLogEntry,
  VoiceRoutingView,
} from '@shared/voice-routing.ts';
import type { OwnA2pContent, OwnA2pView, SavedTeam } from '@shared/types/index.ts';
import type {
  ChecklistDefinition,
  ChecklistStack,
  ChecklistTemplate,
  ChecklistLayer,
  ChecklistVisitType,
} from '@shared/checklists/stack.ts';
import type { InboxActionSuggestion } from '../../shared/inbox-actions.ts';
import type { OperationalReport, OperationalReportId } from '../../shared/operational-reports.ts';
import { API_BASE, ApiRequestError, api, fetchAllPages, queryString } from './client.ts';
import type { ServerPublicKeyCreation, ServerPublicKeyRequest } from '../lib/webauthn.ts';
import type { QrSignInPlatform, QrSignInStatus } from '@shared/qr-sign-in.ts';
import type { FeedbackCategory, FeedbackSort, FeedbackTab } from '@shared/constants/feedback.ts';
import type { LocalizedText } from '@shared/constants/localized-text.ts';
import type { SupportedLanguage } from '@shared/constants/languages.ts';
import type { ImportDuplicateStrategy } from '@shared/schemas/customers.ts';
import type { PhotoQuoteSettingsPatch, PhotoRequestNeed } from '@shared/schemas/photo-quotes.ts';
import type { PhotoIntakeFilter, PhotoQuotePriceKind, RoomFindingValue } from '@shared/photo-quotes.ts';
import type {
  PhotoIntakeDetail,
  PhotoIntakeSummary,
  PhotoQuoteDemo,
  PhotoQuoteSettingsView,
} from '@shared/types/photo-quotes.ts';
import type {
  FeedbackBoardState,
  FeedbackNotification,
  FeedbackPreviewLaunch,
  FeedbackRequestDetail,
  FeedbackRequestSummary,
  FeedbackSimilarRequest,
} from '@shared/types/index.ts';
import type {
  ActivityEvent,
  AddressSuggestion,
  AiSuggestion,
  AiUsageSummary,
  ProviderUsageSummary,
  ApiKey,
  ApiScopeDescriptor,
  ApiSurfaceEntry,
  Appointment,
  AppointmentSeries,
  AvailabilityView,
  BillingState,
  SlotSuggestion,
  SuggestionsEmptyReason,
  CalendarFeed,
  ChangeOrder,
  ChangeOrderResult,
  ChangeOrderSendResult,
  ExtraWorkVisitOption,
  Company,
  CompanyPaymentMethod,
  AssistantConnection,
  ConnectedApp,
  Customer,
  DashboardData,
  SalesDashboardData,
  EmailLogEntry,
  InboundText,
  Inspection,
  InspectionAction,
  Invoice,
  InvoiceBatchSendResult,
  InvoiceDetail,
  JobCostingReport,
  JobProfitReport,
  ContractorSummary,
  QuoteProfitInputs,
  TeamPayMember,
  TeamPayOverview,
  MarginFloorSettings,
  MarginOverrideReport,
  QuoteMarginOverride,
  QuoteMarginView,
  TimeEntry,
  VisitProfitSummary,
  OpenInspectionAction,
  LinkedIdentity,
  MonthlyInvoice,
  MonthlyInvoiceDetail,
  Location,
  MagicQuoteCapture,
  MagicQuoteCoverage,
  MagicQuoteCostSummary,
  MagicQuoteObservation,
  MagicQuoteRoom,
  MagicQuoteSession,
  MagicQuoteSessionSummary,
  LocationOverrides,
  ExchangeRatesResponse,
  LocationSummary,
  NotificationFeed,
  NotificationState,
  Paginated,
  CapacityReport,
  CrewReport,
  LeadsReport,
  OwedOnQuotes,
  SingleJob,
  SingleJobActionResult,
  BookedJob,
  Payment,
  QuotePaymentLedger,
  PaymentSettings,
  PendingOAuthSignup,
  ProfitabilityReport,
  Property,
  PropertyChecklist,
  VisitChecklist,
  VisitChecklistReport,
  Quote,
  QuoteFunnelReport,
  QuoteRequest,
  QuoteSummary,
  ReceivablesReport,
  RetentionReport,
  GroupTemplate,
  RoomTemplate,
  ServiceTemplate,
  LoginResponse,
  SessionResponse,
  SessionUser,
  SearchResults,
  SmsLogEntry,
  SmsSettings,
  TwilioTakeoverOption,
  VisitWorksheet,
  WorksheetRoom,
  WorksheetTask,
} from '@shared/types/index.ts';
import type {
  AnswerQuickQuoteInput,
  GenerateQuickQuoteResponse,
  QuickQuoteCapabilities,
  QuickQuoteCreateInput,
  QuickQuotePhoto,
  QuickQuoteSession,
} from '@shared/quick-quote.ts';
import type {
  AppointmentPaymentStatus,
  AppointmentStatus,
  BillingInterval,
  OAuthProvider,
  PaidPlanId,
  PaymentMethodFamily,
  PlanId,
  QuoteSegment,
  SeriesFrequency,
  ServiceFrequency,
  ServiceMode,
  UserRole,
} from '@shared/constants/index.ts';
import type { PaymentProviderId } from '@shared/constants/payment-providers.ts';
import type { ApiScope } from '@shared/constants/api-keys.ts';
import type { QuoteChangesInput } from '@shared/schemas/quotes.ts';

/** One place that knows the API's URLs and payload shapes. */

// --- Auth -------------------------------------------------------------------

export const authApi = {
  session: () => api.get<SessionResponse>('/auth/session'),
  /**
   * May answer with a session OR with a second-factor challenge — see
   * `isMfaChallenge`. The two are different shapes on purpose: a half-finished
   * sign-in must not be mistakable for a session.
   */
  login: (body: { email: string; password: string; rememberMe: boolean; turnstileToken?: string }) =>
    api.post<LoginResponse>('/auth/login', body),
  /** Ask for a code (SMS) or a WebAuthn request, mid-sign-in. */
  /**
   * What a sign-in that came back from a social provider still owes.
   *
   * The password form already knows its options — they arrived with the
   * password response. A visitor returning from Google does not, and the token
   * that would fetch them is in an HttpOnly cookie they cannot read, so the
   * server reads it for them. `pending: false` when there is nothing waiting.
   */
  mfaPending: () =>
    api.get<{ pending: boolean; options?: ChallengeOption[]; trustedDeviceDays?: number }>('/auth/mfa/pending'),
  mfaChallenge: (body: { loginToken?: string; type: string; methodId?: string | null }) =>
    api.post<{ challengeId: string; type: string; maskedPhone?: string; publicKey?: ServerPublicKeyRequest }>(
      '/auth/mfa/challenge',
      body,
    ),
  /** Answer it. Succeeds with the same session payload an ordinary sign-in returns. */
  mfaVerify: (body: {
    loginToken?: string;
    challengeId: string;
    code?: string;
    assertion?: unknown;
    rememberDevice?: boolean;
  }) => api.post<SessionResponse & { recoveryCodesRemaining?: number }>('/auth/mfa/verify', body),
  register: (body: {
    companyName: string;
    fullName: string;
    email: string;
    password: string;
    timezone?: string;
    language?: string;
    serviceMode?: ServiceMode;
    turnstileToken?: string;
    attribution?: {
      referrer: string | null;
      landingPath: string | null;
      utmSource: string | null;
      utmMedium: string | null;
      utmCampaign: string | null;
      utmTerm: string | null;
      utmContent: string | null;
    };
    phone?: string;
    heardAbout?: string;
    heardAboutDetail?: string;
  }) => api.post<SessionResponse>('/auth/register', body),
  logout: () => api.post<{ signedOut: boolean }>('/auth/logout', {}),
  /**
   * `currentPassword` is omitted when the account has none yet — someone who
   * signed up with a social provider setting one for the first time.
   */
  changePassword: (body: { currentPassword?: string; newPassword: string }) =>
    api.post<{ changed: boolean; csrfToken: string }>('/auth/change-password', body),
  /** The details a signed-in person owns about themselves. */
  updateProfile: (body: { fullName?: string; phone?: string }) => api.patch<SessionUser>('/auth/profile', body),
  requestPasswordReset: (body: { email: string; turnstileToken?: string }) =>
    api.post<{ requested: boolean; message: string }>('/auth/request-password-reset', body),
  resetPassword: (body: { token: string; newPassword: string; turnstileToken?: string }) =>
    api.post<{ reset: boolean }>('/auth/reset-password', body),

  /**
   * Connected social accounts. `available` is what the deployment offers, so
   * Settings can show a connect button only for providers that work.
   *
   * Connecting is not here on purpose: it is a full-page redirect out to the
   * provider, so it is an `<a href>` in the UI (see social-buttons.tsx), not a
   * fetch.
   */
  identities: () => api.get<{ items: LinkedIdentity[]; available: OAuthProvider[] }>('/auth/identities'),
  /** Also revokes the user's other sessions, like a password change does. */
  disconnectIdentity: (provider: OAuthProvider) =>
    api.delete<{ disconnected: boolean; csrfToken: string }>(`/auth/identities/${provider}`),

  /** The verified identity waiting for a company name. 404 once it expires. */
  pendingOauthSignup: () => api.get<PendingOAuthSignup>('/auth/oauth/pending'),
  completeOauthSignup: (body: {
    companyName: string;
    fullName: string;
    phone?: string;
    timezone?: string;
    language?: string;
    serviceMode?: ServiceMode;
    heardAbout?: string;
    heardAboutDetail?: string;
  }) => api.post<SessionResponse & { returnTo?: string }>('/auth/oauth/complete-signup', body),

  /**
   * Switches the location this session works in. Every list in the app is
   * scoped by it, so callers must refetch everything afterwards — the session
   * provider's `switchLocation` is the one place that does both. The response
   * carries the refreshed user because roles can differ per location: the
   * `user` is the role and permissions in force AT the new location, and must
   * be merged into the cached session alongside `location`.
   */
  switchLocation: (locationId: string) =>
    api.post<{
      location: LocationSummary | null;
      locations: LocationSummary[];
      user: SessionUser;
      company?: Company;
    }>('/auth/session/location', { locationId }),
};

/**
 * A role a member holds at one specific location, overriding their company
 * role there. Owners cannot be overridden — they are owners everywhere.
 */
export type LocationRole = Exclude<UserRole, 'owner'>;

export interface TeamMember {
  id: string;
  email: string;
  fullName: string;
  role: string;
  isActive: boolean;
  /** The locations this member may work in — names come from `locationApi.list`. */
  locationIds: string[];
  /**
   * Per-location role overrides, sparse: only locations where the member's
   * role differs from their company role appear. Keys are within
   * `locationIds`; never present for owners.
   */
  locationRoles: Record<string, LocationRole>;
  /** Their own working hours (0059); null follows their branch's. */
  workDayStartLocal: string | null;
  workDayEndLocal: string | null;
  /** Comma-separated weekday numbers, 0 = Sunday. */
  workDays: string | null;
  lastLoginAt: string | null;
}

// --- Files ------------------------------------------------------------------

export interface QuoteFile {
  dustEvidenceEligible?: boolean;
  id: string;
  category: 'attachment' | 'quote_photo';
  originalFilename: string | null;
  contentType: string;
  sizeBytes: number;
  downloadUrl: string;
  createdAt: string;
}

export interface QuoteFileUsage {
  count: number;
  totalBytes: number;
  /** True on plans with unlimited storage; the caps below are then null. */
  unlimited: boolean;
  maxCount: number | null;
  maxTotalBytes: number | null;
}

export const fileApi = {
  listForQuote: (quoteId: string) => api.get<{ items: QuoteFile[]; usage: QuoteFileUsage }>(`/files/quotes/${quoteId}`),
  upload: (quoteId: string, file: File, category: 'attachment' | 'quote_photo') => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    return api.upload<QuoteFile>(`/files/quotes/${quoteId}`, form);
  },
  listForAppointment: (appointmentId: string) =>
    api.get<{ items: QuoteFile[]; usage: QuoteFileUsage }>(`/files/appointments/${appointmentId}`),
  uploadVisitPhoto: (appointmentId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<QuoteFile>(`/files/appointments/${appointmentId}`, form);
  },
  /**
   * Inspection photographs, scoped to the walkthrough rather than the line.
   *
   * Uploading and attaching are two steps on purpose: the upload answers with
   * a file id, the line carries it as `photoFileId`, and the lines are saved
   * declaratively as one list. A photo therefore survives being moved between
   * lines, and an upload is never lost because the save that followed it
   * failed.
   */
  listForInspection: (inspectionId: string) =>
    api.get<{ items: QuoteFile[]; usage: QuoteFileUsage }>(`/files/inspections/${inspectionId}`),
  uploadInspectionPhoto: (inspectionId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<QuoteFile>(`/files/inspections/${inspectionId}`, form);
  },
  remove: (fileId: string) => api.delete<void>(`/files/${fileId}`),
};

// --- Company ----------------------------------------------------------------

export const companyApi = {
  providerUsage: () => api.get<ProviderUsageSummary>('/company/provider-usage'),
  get: () => api.get<Company>('/company'),
  update: (body: Record<string, unknown>) => api.patch<Company>('/company', body),
  /** Creates the public quote-request form's URL, or rotates it to a new one. */
  createIntakeLink: () => api.post<Company>('/company/intake-link', {}),
  disableIntakeLink: () => api.delete<Company>('/company/intake-link'),
  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<Company>('/company/logo', form);
  },
  deleteLogo: () => api.delete<void>('/company/logo'),
  /** This company's own Twilio account. Owner-only in both directions. */
  smsSettings: () => api.get<SmsSettings>('/company/sms-settings'),
  /** Settings › Text messaging › Calls. `available` is whether this installation answers calls. */
  voiceSettings: () =>
    api.get<{ available: boolean; settings: import('../../shared/voice.ts').VoiceSettings }>('/company/voice-settings'),
  saveVoiceSettings: (body: {
    forwardTo: string | null;
    ringTimeoutSeconds: number;
    greetingKind: 'tts' | 'audio';
    greetingText: string | null;
    recordAnswered: boolean;
    transcribe: boolean;
    voicemailNotify: import('../../shared/voice.ts').VoicemailNotify;
    afterHours: import('../../shared/voice.ts').AfterHoursPolicy;
  }) =>
    api.put<{ available: boolean; settings: import('../../shared/voice.ts').VoiceSettings }>(
      '/company/voice-settings',
      body,
    ),
  /** Settings › Calls › Numbers, teams and locations: which phones each number rings. */
  voiceRouting: () => api.get<VoiceRoutingView>('/company/voice-routing'),
  /** Numbers on the company's own Twilio account that are not in the list yet. */
  voiceAccountNumbers: () => api.get<{ numbers: AccountNumber[] }>('/company/voice-routing/account-numbers'),
  addVoiceNumber: (body: { phoneNumber: string; label?: string }) =>
    api.post<VoiceRoutingView>('/company/voice-routing/numbers', body),
  updateVoiceNumber: (id: string, body: UpdateVoiceNumberInput) =>
    api.patch<VoiceRoutingView>(`/company/voice-routing/numbers/${encodeURIComponent(id)}`, body),
  removeVoiceNumber: (id: string) =>
    api.delete<VoiceRoutingView>(`/company/voice-routing/numbers/${encodeURIComponent(id)}`),
  createVoiceTeam: (body: SaveVoiceTeamInput) => api.post<VoiceRoutingView>('/company/voice-routing/teams', body),
  updateVoiceTeam: (id: string, body: SaveVoiceTeamInput) =>
    api.patch<VoiceRoutingView>(`/company/voice-routing/teams/${encodeURIComponent(id)}`, body),
  deleteVoiceTeam: (id: string) =>
    api.delete<VoiceRoutingView>(`/company/voice-routing/teams/${encodeURIComponent(id)}`),
  createIvrMenu: (body: SaveIvrMenuInput) => api.post<VoiceRoutingView>('/company/voice-routing/ivr-menus', body),
  updateIvrMenu: (id: string, body: SaveIvrMenuInput) =>
    api.patch<VoiceRoutingView>(`/company/voice-routing/ivr-menus/${encodeURIComponent(id)}`, body),
  deleteIvrMenu: (id: string) =>
    api.delete<VoiceRoutingView>(`/company/voice-routing/ivr-menus/${encodeURIComponent(id)}`),
  saveLocationVoiceDefaults: (locationId: string, body: SaveLocationVoiceInput) =>
    api.put<VoiceRoutingView>(`/company/voice-routing/locations/${encodeURIComponent(locationId)}`, body),
  /** The call log: which number, when, how long, and who took it. */
  voiceCallLog: (query: { numberId?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (query.numberId) params.set('numberId', query.numberId);
    if (query.limit) params.set('limit', String(query.limit));
    const suffix = params.toString();
    return api.get<{ calls: VoiceCallLogEntry[] }>(`/company/voice-routing/calls${suffix ? `?${suffix}` : ''}`);
  },
  uploadVoiceGreeting: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload<{ available: boolean; settings: import('../../shared/voice.ts').VoiceSettings }>(
      '/company/voice-settings/greeting',
      form,
    );
  },
  deleteVoiceGreeting: () =>
    api.delete<{ available: boolean; settings: import('../../shared/voice.ts').VoiceSettings }>(
      '/company/voice-settings/greeting',
    ),
  voiceGreetingUrl: () => `${API_BASE}/company/voice-settings/greeting`,
  /** Recordings left on a Twilio account the company let go. Answers whatever the plan. */
  voiceRecordingsLeft: () => api.get<{ recordingsLeftOnTwilio: number }>('/company/voice-settings/recordings-left'),
  saveSmsSettings: (body: {
    accountSid?: string;
    authToken?: string;
    apiKeySid?: string;
    apiKeySecret?: string;
    messagingServiceSid?: string;
    fromNumber?: string;
    enabled?: boolean;
  }) => api.patch<SmsSettings>('/company/sms-settings', body),
  disconnectSms: () => api.delete<SmsSettings>('/company/sms-settings'),
  /**
   * "Set up Twilio again" on the own-account card, or, with `confirm`, the
   * owner agreeing to a takeover the card offered (`twilioSetup.takeover`).
   */
  twilioSetup: (confirm?: { option: TwilioTakeoverOption; target: string }) =>
    api.post<SmsSettings>('/company/sms-settings/twilio-setup', confirm ? { confirm } : {}),
  /**
   * "Connect your Twilio account": Twilio's approval page for BlastoClean's
   * Connect App. The whole page leaves for it, and Twilio sends the owner back
   * to Settings with a `?twilioConnect=` flag.
   */
  twilioConnectStart: () => api.post<{ url: string }>('/company/sms-settings/connect/start', {}),
  /** Buys the texting number of the account connected through Twilio Connect, in this area code. */
  buyTwilioConnectNumber: (areaCode: string) =>
    api.post<SmsSettings>('/company/sms-settings/connect/number', { areaCode }),
  /** The texting registration (A2P 10DLC) of the company's own Twilio account. */
  ownA2p: () => api.get<OwnA2pView>('/company/sms-settings/a2p'),
  /** Files it, or sends a rejected one back, on the account's approved business registration. */
  submitOwnA2p: (body: { brandSid: string; content: OwnA2pContent; refile?: string }) =>
    api.post<OwnA2pView>('/company/sms-settings/a2p', body),
  /** Asks Twilio now how it stands (and takes on a campaign the owner filed themselves). */
  refreshOwnA2p: () => api.post<OwnA2pView>('/company/sms-settings/a2p/refresh', {}),
  /**
   * Asks the operator to issue this company a sending number of its own.
   *
   * Returns the settings straight back with a `pending` managed number rather
   * than waiting: the number is bought and registered by a cron, because
   * carrier approval takes days to weeks. The page polls `smsSettings` until
   * the status settles.
   */
  requestManagedNumber: (body: {
    areaCode?: string;
    registration?: {
      kind: 'standard' | 'sole_proprietor';
      legalName: string;
      ein?: string;
      businessType?: string;
      addressLine1: string;
      addressLine2?: string;
      city: string;
      region: string;
      postalCode: string;
      website?: string;
      contactFirstName: string;
      contactLastName: string;
      contactEmail: string;
      contactPhone: string;
      mobilePhone?: string;
    };
  }) => api.post<SmsSettings>('/company/sms-settings/managed-number', body),
  /** Asks for the YES text to be sent again. Sole owners only, while we are waiting on that reply. */
  resendManagedNumberConfirmation: () =>
    api.post<SmsSettings>('/company/sms-settings/managed-number/resend-confirmation', {}),
  /**
   * One message to a number the sender nominates, so a setup can be proved
   * before a real reminder depends on it. `failureReason` is the provider's own
   * words — the whole point of the button is to see them.
   */
  sendTestSms: (recipientPhone: string) =>
    api.post<{
      sentTo: string;
      delivered: boolean;
      failureReason: string | null;
    }>('/company/sms-settings/test', {
      recipientPhone,
    }),
  users: () => api.get<{ items: TeamMember[] }>('/company/users'),
  // `locationRoles` is the full override map for the member (sparse — a
  // location absent from it uses the company role). Only valid for non-owner
  // members, and keys must be within `locationIds`. Sending `role` or
  // `locationRoles` revokes the member's sessions server-side.
  createUser: (body: {
    email: string;
    fullName: string;
    role: string;
    password: string;
    locationIds?: string[];
    locationRoles?: Record<string, LocationRole>;
  }) => api.post<TeamMember>('/company/users', body),
  updateUser: (
    userId: string,
    body: {
      role?: string;
      isActive?: boolean;
      fullName?: string;
      locationIds?: string[];
      locationRoles?: Record<string, LocationRole>;
      workDayStartLocal?: string | null;
      workDayEndLocal?: string | null;
      workDays?: number[] | null;
    },
  ) => api.patch<TeamMember>(`/company/users/${userId}`, body),
};

// --- Margin-gated discounts (0118) ---------------------------------------------

/**
 * The margin floor, each quote line's expected margin, and the override trail.
 * Staff only: none of this is ever on a customer page.
 */
export const marginApi = {
  settings: () => api.get<MarginFloorSettings>('/margin/settings'),
  updateSettings: (body: {
    floorBasisPoints?: number;
    mode?: 'off' | 'block' | 'override';
    defaultLoadedHourlyCents?: number | null;
  }) => api.patch<MarginFloorSettings>('/margin/settings', body),
  quote: (quoteId: string) => api.get<QuoteMarginView>(`/margin/quotes/${quoteId}`),
  preview: (quoteId: string, body: { discountType: 'fixed' | 'percentage' | null; discountValue: number }) =>
    api.post<QuoteMarginView>(`/margin/quotes/${quoteId}/preview`, body),
  requestOverride: (
    quoteId: string,
    body: { discountType: 'fixed' | 'percentage'; discountValue: number; reason: string; expectedVersion: number },
  ) =>
    api.post<{ applied: boolean; override: QuoteMarginOverride | null; quote: Quote | null }>(
      `/margin/quotes/${quoteId}/overrides`,
      body,
    ),
  overrides: (params: { status?: 'pending' | 'approved' | 'rejected'; quoteId?: string; limit?: number } = {}) =>
    api.get<QuoteMarginOverride[]>(`/margin/overrides${queryString(params)}`),
  approve: (overrideId: string, note?: string) =>
    api.post<{ override: QuoteMarginOverride; quote: Quote }>(`/margin/overrides/${overrideId}/approve`, {
      note: note ?? null,
    }),
  reject: (overrideId: string, note?: string) =>
    api.post<QuoteMarginOverride>(`/margin/overrides/${overrideId}/reject`, { note: note ?? null }),
  report: (params: { from?: string; to?: string } = {}) =>
    api.get<MarginOverrideReport>(`/margin/overrides/report${queryString(params)}`),
};

// --- Team pay (0035) ------------------------------------------------------------

/**
 * Settings → Team pay and each member's worker type, W-9 date and pay rates.
 * Reading is the owner's and managers'; every write is the owner's.
 */
export const teamPayApi = {
  overview: () => api.get<TeamPayOverview>('/team-pay'),
  updateSettings: (body: {
    defaultBurdenBasisPoints?: number | null;
    targetMarginBasisPoints?: number | null;
    suppliesBasisPoints?: number | null;
    ownerCostEnabled?: boolean;
    ownerHourlyCents?: number | null;
    cardProcessingBasisPoints?: number;
    cardProcessingFixedCents?: number;
    accountantExportEmailEnabled?: boolean;
  }) => api.patch<TeamPayOverview>('/team-pay/settings', body),
  updateMember: (
    userId: string,
    body: {
      workerType?: 'w2' | '1099' | null;
      burdenOverrideBasisPoints?: number | null;
      w9ReceivedOn?: string | null;
    },
  ) => api.patch<TeamPayMember>(`/team-pay/members/${userId}`, body),
  addRate: (
    userId: string,
    body:
      | { payType: 'hourly' | 'per_job'; rateCents: number; effectiveFrom: string }
      | { payType: 'percent'; rateBasisPoints: number; effectiveFrom: string }
      | { payType: 'salary'; annualSalaryCents: number; salaryHoursPerYear?: number; effectiveFrom: string },
  ) => api.post<{ member: TeamPayMember; recostedVisits: number }>(`/team-pay/members/${userId}/rates`, body),
  deleteRate: (userId: string, rateId: string) =>
    api.delete<{ member: TeamPayMember; recostedVisits: number }>(`/team-pay/members/${userId}/rates/${rateId}`),
};

// --- Locations ----------------------------------------------------------------

export interface LocationUsage {
  customers: number;
  quotes: number;
  appointments: number;
}

/**
 * A PATCH body: `name` renames, and every override field accepts a value (to
 * override the company setting) or null (back to inheriting it). Absent
 * fields stay untouched.
 */
export type LocationUpdateBody = Partial<{ name: string } & LocationOverrides>;

/**
 * The location DTO as the API returns it: the shared shape plus `intakePath`,
 * the location's own public quote-request address (null when it has none and
 * leads arrive through the company-wide form only). Spelled as an
 * intersection here so the web side can build against the contract while the
 * worker's shared-type change lands in parallel.
 */
export type LocationWithIntakeLink = Location & { intakePath: string | null };

export const locationApi = {
  list: () => api.get<{ locations: LocationWithIntakeLink[] }>('/locations'),
  get: (id: string) => api.get<{ location: LocationWithIntakeLink; usage: LocationUsage }>(`/locations/${id}`),
  /** 403 PLAN_REQUIRED unless the company's effective plan carries multi_location. */
  create: (body: { name: string }) => api.post<{ location: LocationWithIntakeLink }>('/locations', body),
  update: (id: string, body: LocationUpdateBody) =>
    api.patch<{ location: LocationWithIntakeLink }>(`/locations/${id}`, body),
  archive: (id: string) => api.post<{ location: LocationWithIntakeLink }>(`/locations/${id}/archive`, {}),
  restore: (id: string) => api.post<{ location: LocationWithIntakeLink }>(`/locations/${id}/restore`, {}),
  /** Mints this location's public form address, or rotates it to a new one. */
  createIntakeLink: (id: string) => api.post<{ location: LocationWithIntakeLink }>(`/locations/${id}/intake-link`, {}),
  removeIntakeLink: (id: string) => api.delete<{ location: LocationWithIntakeLink }>(`/locations/${id}/intake-link`),
};

// --- Exchange rates (0115) ---------------------------------------------------

export const exchangeRateApi = {
  list: () => api.get<ExchangeRatesResponse>('/exchange-rates'),
  /** A rate typed by hand, or `automatic` to follow the daily reference rate. */
  set: (
    currencyCode: string,
    body: { source: 'manual'; rate: string; baseCurrencyCode?: string } | { source: 'automatic' },
  ) => api.put<ExchangeRatesResponse>(`/exchange-rates/${currencyCode}`, body),
  remove: (currencyCode: string) => api.delete<ExchangeRatesResponse>(`/exchange-rates/${currencyCode}`),
  refresh: () => api.post<ExchangeRatesResponse>('/exchange-rates/refresh', {}),
};

// --- Customers and properties ----------------------------------------------

export interface DuplicateMatch {
  id: string;
  displayName: string;
  email: string | null;
  phone: string | null;
}

/** What became of one row of an import file. */
export interface ImportRowResult {
  /** The line in the person's own file, not the position in the batch. */
  line: number;
  status: 'created' | 'updated' | 'skipped' | 'failed';
  customerId?: string;
  reason?: 'duplicate' | 'invalid' | 'unknown_id' | 'empty' | 'ambiguous';
  message?: string;
  /** Zod's per-field messages, so the preview can mark the offending cell. */
  fields?: Record<string, string>;
  /** The customer a duplicate row landed on. */
  matched?: { id: string; displayName: string };
}

export interface CustomerImportResult {
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  dryRun: boolean;
  results: ImportRowResult[];
}

export interface CustomerImportRequest {
  rows: Array<{ line: number; id?: string | null; values: Record<string, unknown> }>;
  duplicateStrategy: ImportDuplicateStrategy;
  addBillingAddressAsProperty?: boolean;
  dryRun?: boolean;
}

export const customerApi = {
  list: (params: { page?: number; pageSize?: number; q?: string }) =>
    api.get<Paginated<Customer>>(`/customers${queryString({ ...params })}`),
  /**
   * The customer list as a file. A plain same-origin `<a href>` like the
   * reports downloads — the session cookie authenticates it and
   * `content-disposition` names the file.
   */
  exportUrl: (format: 'csv' | 'xlsx') => `${API_BASE}/customers/export${queryString({ format })}`,
  /**
   * One batch of an import. `dryRun` asks what would happen without writing
   * anything, which is what the review step runs to find out how many of these
   * people are already customers.
   */
  import: (body: CustomerImportRequest) => api.post<CustomerImportResult>('/customers/import', body),
  get: (id: string) => api.get<Customer>(`/customers/${id}`),
  /** Whether the office can call this customer from the company's number, and which phone rings first. */
  callOptions: (id: string) => api.get<CustomerCallOptions>(`/customers/${id}/call`),
  /**
   * Rings the person's phone (or `ringPhone`), then calls the customer from
   * the company's number. The same `idempotencyKey` again finds that call
   * instead of placing another.
   */
  startCall: (id: string, ringPhone?: string, idempotencyKey?: string) =>
    api.post<StartedCustomerCall>(`/customers/${id}/call`, ringPhone ? { ringPhone } : {}, idempotencyKey),
  create: (body: Record<string, unknown>) => api.post<Customer>('/customers', body),
  update: (id: string, body: Record<string, unknown>) => api.patch<Customer>(`/customers/${id}`, body),
  remove: (id: string) => api.delete<void>(`/customers/${id}`),
  /** "Does this person already exist?", asked while the create form is typed. */
  checkDuplicates: (params: { email?: string; phone?: string }) =>
    api.get<{ matches: DuplicateMatch[] }>(`/customers/check-duplicates${queryString({ ...params })}`),
  /** The portal URL exists once, in this response — only its hash is stored. */
  createPortalLink: (id: string) => api.post<{ customer: Customer; url: string }>(`/customers/${id}/portal-link`, {}),
  /**
   * Mints a link and emails it to the customer. Always a fresh link — the
   * previous one was never kept in a form anyone could send — so this replaces
   * whatever the customer had, exactly as minting does.
   */
  emailPortalLink: (id: string, body: { recipientEmail?: string; message?: string }) =>
    api.post<{ customer: Customer; url: string; queuedFor: string }>(`/customers/${id}/portal-link/email`, body),
  /** The same send by text. Also mints a fresh link, so it also replaces one. */
  textPortalLink: (id: string, body: { recipientPhone?: string; message?: string }) =>
    api.post<{
      customer: Customer;
      url: string;
      queuedFor: string;
      segments: number;
      noteTruncated: boolean;
      delivered: boolean;
      deferredUntilOpenHours: boolean;
      textUnconfirmed?: boolean;
      failureReason?: string | null;
    }>(`/customers/${id}/portal-link/text`, body),
  revokePortalLink: (id: string) => api.delete<Customer>(`/customers/${id}/portal-link`),
  /**
   * Every property, not the first page. This backs the property picker on the
   * new-quote and new-series pages, where a missing entry reads as "this
   * customer has no such address" and sends someone off to retype it.
   */
  properties: (id: string) =>
    fetchAllPages<Property>((page, pageSize) =>
      api.get<Paginated<Property>>(`/customers/${id}/properties${queryString({ page, pageSize })}`),
    ),
  createProperty: (id: string, body: Record<string, unknown>) =>
    api.post<Property>(`/customers/${id}/properties`, body),
  quotes: (id: string, params: { page?: number; pageSize?: number } = {}) =>
    api.get<Paginated<QuoteSummary>>(`/customers/${id}/quotes${queryString({ ...params })}`),
};

export const propertyApi = {
  get: (id: string) => api.get<Property>(`/properties/${id}`),
  update: (id: string, body: Record<string, unknown>) => api.patch<Property>(`/properties/${id}`, body),
  remove: (id: string) => api.delete<void>(`/properties/${id}`),
  checklist: (id: string) => api.get<{ checklist: PropertyChecklist | null }>(`/properties/${id}/checklist`),
  updateChecklist: (id: string, body: Record<string, unknown>) =>
    api.put<{ checklist: PropertyChecklist }>(`/properties/${id}/checklist`, body),
  addChecklistItem: (id: string, body: Record<string, unknown>) =>
    api.post<{ checklist: PropertyChecklist }>(`/properties/${id}/checklist/items`, body),
  updateChecklistItem: (id: string, itemId: string, body: Record<string, unknown>) =>
    api.patch<{ checklist: PropertyChecklist }>(`/properties/${id}/checklist/items/${itemId}`, body),
  deleteChecklistItem: (id: string, itemId: string, version: number) =>
    api.delete<{ checklist: PropertyChecklist }>(`/properties/${id}/checklist/items/${itemId}`, { version }),
  checklistFromQuote: (id: string, quoteId: string, version?: number) =>
    api.post<{ checklist: PropertyChecklist }>(`/properties/${id}/checklist/from-quote/${quoteId}`, {
      ...(version ? { version } : {}),
    }),
  checklistFromTemplates: (id: string, version?: number) =>
    api.post<{ checklist: PropertyChecklist }>(`/properties/${id}/checklist/from-templates`, {
      ...(version ? { version } : {}),
    }),
  checklistHistory: (id: string) => api.get<{ visits: VisitChecklistReport[] }>(`/properties/${id}/checklist/history`),
};

/**
 * Address suggestions for the property form. Only worth calling on a
 * deployment whose `/config` says `addressAutocomplete` — everywhere else this
 * answers an empty list, which is a round trip for nothing.
 *
 * `q` is the street being typed; the rest is whatever the form already holds,
 * and it is what lets the free provider match at all — it identifies an
 * address nationally rather than completing a fragment.
 */
export const addressApi = {
  suggest: (params: { q: string; city?: string; state?: string; postalCode?: string }) =>
    api.get<{ items: AddressSuggestion[] }>(`/addresses/suggest${queryString({ ...params })}`),
};

// --- Customer re-engagement -------------------------------------------------

export type ReengagementStatus = 'open' | 'snoozed' | 'contacted' | 'dismissed';
export type ReengagementListStatus = ReengagementStatus | 'all';
export type ReengagementReason = 'inactive' | 'repeat_cancellations';
export type ReengagementContactChannel = 'phone' | 'sms' | 'email' | 'other';

export interface ReengagementChannelAvailability {
  allowed: boolean;
  reason: string | null;
}

export interface ReengagementCandidate {
  id: string;
  customerId: string;
  customerName: string;
  firstName: string | null;
  phone: string | null;
  email: string | null;
  reasons: ReengagementReason[];
  lastCompletedAt: string | null;
  daysSinceLastClean: number | null;
  cancellationCount: number;
  cancellationWindowDays: number;
  latestCancellationAt: string | null;
  status: ReengagementStatus;
  snoozedUntil: string | null;
  openedAt: string;
  channels: {
    call: ReengagementChannelAvailability;
    sms: ReengagementChannelAvailability;
    email: ReengagementChannelAvailability;
  };
  outreach: {
    smsBody: string;
    emailSubject: string;
    emailBody: string;
  };
}

export interface ReengagementSummary {
  open: number;
  snoozed: number;
  contacted: number;
  dismissed: number;
  total: number;
}

export interface ReengagementPage extends Paginated<ReengagementCandidate> {
  summary: ReengagementSummary;
}

export interface ReengagementSettings {
  enabled: boolean;
  inactiveDays: number;
  cancellationCount: number;
  cancellationWindowDays: number;
  reminderCadenceDays: number;
  notificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
  smsNotificationsEnabled: boolean;
  /** Computed by the server from the company's notification configuration. */
  notificationsAvailable: boolean;
}

export type ReengagementUpdate =
  | { action: 'contacted'; channel: ReengagementContactChannel }
  | { action: 'snooze'; until: string }
  | {
      action: 'dismiss';
      reason: 'do_not_contact' | 'bad_contact' | 'not_interested' | 'other';
    };

/**
 * Re-engagement is deliberately network-only. A stale queue can prompt a
 * second outreach after another device already contacted or dismissed the
 * customer, so these reads never fall back to the durable GET cache.
 */
export const reengagementApi = {
  list: (
    params: {
      page?: number;
      pageSize?: number;
      status?: ReengagementListStatus;
    } = {},
  ) => api.getFresh<ReengagementPage>(`/reengagement${queryString({ ...params })}`),
  summary: () => api.getFresh<ReengagementSummary>('/reengagement/summary'),
  settings: () => api.getFresh<ReengagementSettings>('/reengagement/settings'),
  saveSettings: (body: Omit<ReengagementSettings, 'notificationsAvailable'>) =>
    api.patch<ReengagementSettings>('/reengagement/settings', body),
  update: (id: string, body: ReengagementUpdate) => api.patch<ReengagementCandidate>(`/reengagement/${id}`, body),
};

// --- Feature feedback board -------------------------------------------------

/**
 * The feedback board is deliberately network-only, like re-engagement above and
 * for a related reason: every number on it is other people's — a vote count
 * that moved, a status that changed — and a cached board would show somebody a
 * "Being worked on" badge that stopped being true a week ago. It is also the
 * one screen in the app that is useless offline: nothing here can be queued
 * and replayed, because a vote is a fact about a moment.
 */
export const feedbackApi = {
  list: (
    params: {
      tab?: FeedbackTab;
      sort?: FeedbackSort;
      category?: FeedbackCategory;
      q?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) => api.getFresh<Paginated<FeedbackRequestSummary>>(`/feedback${queryString({ ...params })}`),
  state: () => api.getFresh<FeedbackBoardState>('/feedback/state'),
  similar: (title: string) =>
    api.getFresh<{ items: FeedbackSimilarRequest[] }>(`/feedback/similar${queryString({ title })}`),
  mine: () => api.getFresh<{ items: FeedbackRequestSummary[] }>('/feedback/my'),
  detail: (id: string) => api.getFresh<{ request?: FeedbackRequestDetail; redirectTo?: string }>(`/feedback/${id}`),
  submit: (body: { title: string; description: string; category?: FeedbackCategory | null }) =>
    api.post<{ request: FeedbackRequestSummary | null; moderationRequired: boolean }>('/feedback', body),
  vote: (id: string) => api.post<{ request: FeedbackRequestSummary | null }>(`/feedback/${id}/vote`),
  unvote: (id: string) => api.delete<{ request: FeedbackRequestSummary }>(`/feedback/${id}/vote`),
  follow: (id: string) => api.post<void>(`/feedback/${id}/follow`),
  unfollow: (id: string) => api.delete<void>(`/feedback/${id}/follow`),
  notifications: () =>
    api.getFresh<{ items: FeedbackNotification[]; unread: number; total: number }>('/feedback/notifications'),
  markNotificationsRead: (ids?: string[]) => api.post<void>('/feedback/notifications/read', ids ? { ids } : {}),
  startPreview: (id: string) => api.post<FeedbackPreviewLaunch>(`/feedback/${id}/preview-session`),
  respondToPreview: (id: string, body: { rating: 'positive' | 'neutral' | 'negative'; feedback?: string | null }) =>
    api.post<{ recordedAt: string }>(`/feedback/${id}/preview-response`, body),
  /** Only ever answers on the preview deployment; elsewhere `previewing` is null. */
  previewContext: () =>
    api.getFresh<{ previewing: { requestId: string; title: string; feedbackUrl: string | null } | null }>(
      '/feedback/preview-context',
    ),
};

/**
 * The bell. `state` is the one the header polls, so it is the smallest
 * answer the server can give; both reads skip the offline cache
 * (`getFresh`) because a badge served from last week's snapshot is worse
 * than no badge.
 */
export const notificationsApi = {
  state: () => api.getFresh<NotificationState>('/notifications/state'),
  list: (params: { page?: number; pageSize?: number; filter?: 'all' | 'unread' } = {}) =>
    api.getFresh<NotificationFeed>(`/notifications${queryString({ ...params })}`),
  /** No ids = mark everything currently visible to this session as read. */
  markRead: (ids?: string[]) => api.post<void>('/notifications/read', ids && ids.length > 0 ? { ids } : {}),
};

// --- Templates --------------------------------------------------------------

export const templateApi = {
  /**
   * Both walk every page: these are the quote editor's room and task pickers,
   * and a truncated picker hides templates the company has defined.
   *
   * `includeInactive` is what Settings uses to show — and restore — a room type
   * or task that was hidden. Everywhere a quote is built, the default holds and
   * hidden templates stay out of the way.
   */
  rooms: (includeInactive = false) =>
    fetchAllPages<RoomTemplate>((page, pageSize) =>
      api.get<Paginated<RoomTemplate>>(
        `/room-templates${queryString({ includeInactive: includeInactive ? 'true' : undefined, page, pageSize })}`,
      ),
    ),
  services: (includeInactive = false) =>
    fetchAllPages<ServiceTemplate>((page, pageSize) =>
      api.get<Paginated<ServiceTemplate>>(
        `/service-templates${queryString({ includeInactive: includeInactive ? 'true' : undefined, page, pageSize })}`,
      ),
    ),
  createService: (body: Record<string, unknown>) => api.post<ServiceTemplate>('/service-templates', body),
  updateService: (id: string, body: Record<string, unknown>) =>
    api.patch<ServiceTemplate>(`/service-templates/${id}`, body),
  deactivateService: (id: string) => api.delete<void>(`/service-templates/${id}`),
  createRoom: (body: Record<string, unknown>) => api.post<RoomTemplate>('/room-templates', body),
  updateRoom: (id: string, body: Record<string, unknown>) => api.patch<RoomTemplate>(`/room-templates/${id}`, body),
  deactivateRoom: (id: string) => api.delete<void>(`/room-templates/${id}`),
  /**
   * The company's complete room-type ordering, hidden ones included, applied
   * as one transaction. Sending the whole list rather than the moved row is
   * what keeps a hidden room type's place in the sequence.
   */
  reorderRooms: (ids: string[]) => api.post<{ items: RoomTemplate[] }>('/room-templates/reorder', { ids }),
  groups: (includeInactive = false) =>
    fetchAllPages<GroupTemplate>((page, pageSize) =>
      api.get<Paginated<GroupTemplate>>(
        `/group-templates${queryString({ includeInactive: includeInactive ? 'true' : undefined, page, pageSize })}`,
      ),
    ),
  createGroup: (body: Record<string, unknown>) => api.post<GroupTemplate>('/group-templates', body),
  deactivateGroup: (id: string) => api.delete<void>(`/group-templates/${id}`),
  restoreGroup: (id: string) => api.patch<GroupTemplate>(`/group-templates/${id}`, { isActive: true }),
  setGroupFavorite: (id: string, isFavorite: boolean) =>
    api.patch<GroupTemplate>(`/group-templates/${id}`, { isFavorite }),
  updateGroup: (id: string, body: Record<string, unknown>) => api.patch<GroupTemplate>(`/group-templates/${id}`, body),
  /**
   * Inserts starter rooms, tasks and groups the company does not already
   * have. Matched rows — by seed key or by name — are left untouched.
   */
  importStarter: () => api.post<{ services: number; rooms: number; groups: number }>('/library/import-starter', {}),
};

// --- Scheduling ---------------------------------------------------------------

export interface SeriesHorizonInput {
  horizonMode: 'company_default' | 'until_date' | 'count';
  horizonUntil: string | null;
  horizonCount: number | null;
}

export interface HorizonRemovalPreview {
  removeCount: number;
  protectedCount: number;
  previewToken: string;
}

export interface SeriesHorizonPreview extends SeriesHorizonInput, HorizonRemovalPreview {
  companyHorizonMonths: 3 | 6 | 9;
  windowEnd: string;
}

export interface RecurringHorizonPreview extends HorizonRemovalPreview {
  months: 3 | 6 | 9;
  seriesCount: number;
}

export interface SeriesAppointmentsPage extends Paginated<Appointment> {
  /** Optional while older backends still return a first page without snapshot support. */
  snapshotAt?: string;
  snapshotToken?: string;
}

export const scheduleApi = {
  /**
   * A date range, not a page — the calendar draws whatever it gets back.
   * `truncated` says the range held more visits than one response will carry,
   * which the page has to show rather than swallow: a visit missing from the
   * grid is one nobody can select, reschedule or complete.
   */
  appointments: (params: {
    from: string;
    to: string;
    assignedUserId?: string;
    paymentStatus?: AppointmentPaymentStatus;
    teamFlexibleOnly?: boolean;
  }) =>
    api.get<{ items: Appointment[]; truncated: boolean }>(
      `/schedule/appointments${queryString({ ...params, teamFlexibleOnly: params.teamFlexibleOnly === undefined ? undefined : String(params.teamFlexibleOnly) })}`,
    ),
  /**
   * Visits still ahead whose customer asked for a different time, soonest
   * first. Always a live read: each row is a decision, and an offline copy
   * would offer to answer requests somebody has already answered.
   */
  changeRequests: () => api.getFresh<{ items: Appointment[]; truncated: boolean }>('/schedule/change-requests'),
  /**
   * The booking grid's free/busy picture: what would block a slot, per person
   * (or for the whole company when `userIds` is empty), plus external busy
   * intervals and how much the external half can be trusted.
   */
  availability: (params: {
    from: string;
    to: string;
    userIds?: string[];
    excludeAppointmentId?: string;
    excludeSeriesId?: string;
    teamId?: string | null;
  }) =>
    api.get<AvailabilityView>(
      `/schedule/availability${queryString({
        from: params.from,
        to: params.to,
        ...(params.teamId ? { teamId: params.teamId } : {}),
        ...(params.userIds && params.userIds.length > 0 ? { userIds: params.userIds.join(',') } : {}),
        ...(params.excludeAppointmentId ? { excludeAppointmentId: params.excludeAppointmentId } : {}),
        ...(params.excludeSeriesId ? { excludeSeriesId: params.excludeSeriesId } : {}),
      })}`,
    ),
  appointment: (id: string) => api.get<Appointment>(`/schedule/appointments/${id}`),
  /** The Job profit card (0035): owner and managers only, never cached on a phone. */
  profit: (id: string) => api.get<{ profit: VisitProfitSummary | null }>(`/schedule/appointments/${id}/profit`),
  /** The office adding time somebody never punched. */
  addTimeEntry: (
    appointmentId: string,
    body: { userId: string; clockedInAt: string; clockedOutAt: string; unpaid?: boolean },
  ) =>
    api.post<{ entry: TimeEntry; timeEntries: TimeEntry[] }>(
      `/schedule/appointments/${appointmentId}/time-entries`,
      body,
    ),
  /** The office correcting a punch: a missed clock-out, a wrong time, unpaid time. */
  updateTimeEntry: (
    appointmentId: string,
    entryId: string,
    body: { clockedInAt?: string; clockedOutAt?: string; unpaid?: boolean },
  ) =>
    api.patch<{ entry: TimeEntry; timeEntries: TimeEntry[] }>(
      `/schedule/appointments/${appointmentId}/time-entries/${entryId}`,
      body,
    ),
  slotOffer: (id: string) =>
    api.get<{
      id: string;
      status: string;
      day: string;
      time: string;
      deadline: string;
      customerName: string | null;
      startsAt: string;
    }>(`/slot-offers/${id}`),
  approveSlotOffer: (id: string) => api.post<void>(`/slot-offers/${id}/approve`, {}),
  declineSlotOffer: (id: string) => api.post<void>(`/slot-offers/${id}/decline`, {}),
  reschedule: (id: string, body: Record<string, unknown>) =>
    withAvailabilityConfirmation((token) =>
      api.patch<Appointment>(`/schedule/appointments/${id}`, {
        ...body,
        ...(token ? { availabilityConfirmation: token } : {}),
      }),
    ),
  /** `expectedChangeRequestedAt`: keep the time as the answer to that request; refused (409) once it has moved on. */
  confirm: (id: string, body: { expectedChangeRequestedAt?: string } = {}) =>
    api.post<Appointment>(`/schedule/appointments/${id}/confirm`, body),
  cancelAppointment: (
    id: string,
    body: {
      cancellationInitiator: 'customer' | 'company' | 'unknown';
      reason?: string;
      /** Omitted, the visit is canceled and a fee that applies stays undecided. */
      lateFeeAction?: 'charge' | 'waive';
      /** The late fee on the screen, in minor units. Required when charging; checked when waiving if sent. */
      lateFeeAmountCents?: number;
      /** Principal plus the card-processing fee shown on the screen. Required when charging. */
      lateFeeChargeTotalCents?: number;
      /** The saved card the screen named, when it named one: a replaced card is refused. */
      lateFeeCardLast4?: string;
      lateFeeCardBrand?: string;
    } = { cancellationInitiator: 'unknown' },
  ) => api.post<Appointment>(`/schedule/appointments/${id}/cancel`, body),
  /** The expected figures, when sent, are what the screen confirmed: anything else is refused (409). */
  chargeLateFee: (
    id: string,
    expected: {
      expectedAmountCents?: number;
      expectedTotalCents?: number;
      expectedCardLast4?: string;
      expectedCardBrand?: string;
    } = {},
  ) => api.post<Appointment>(`/schedule/appointments/${id}/late-fee/charge`, expected),
  /** `expectedAmountCents`, when sent, is what the screen asked to waive: anything else is refused (409). */
  waiveLateFee: (id: string, expected: { expectedAmountCents?: number } = {}) =>
    api.post<Appointment>(`/schedule/appointments/${id}/late-fee/waive`, expected),
  /** Creates the late-fee invoice when there is no card to charge. Does not charge it. */
  ensureLateFeeInvoice: (id: string) => api.post<Appointment>(`/schedule/appointments/${id}/late-fee/invoice`, {}),
  /**
   * Closes a visit out. `actualMinutes` records how long it really took, and
   * `priceCents` corrects what it costs — sent only when the office actually
   * changed the figure, so an untouched dialog goes on posting `{}` and the
   * visit keeps the price its schedule gave it. Zero IS a correction ("no
   * charge"), so the guard is against `undefined`, not falsiness.
   */
  complete: (
    id: string,
    actualMinutes?: number | null,
    priceCents?: number,
    completionOverrideReason?: string,
    checklistRevision?: number,
  ) =>
    api.post<Appointment>(`/schedule/appointments/${id}/complete`, {
      ...(actualMinutes ? { actualMinutes } : {}),
      ...(priceCents === undefined ? {} : { priceCents }),
      ...(completionOverrideReason === undefined ? {} : { completionOverrideReason }),
      ...(checklistRevision === undefined ? {} : { checklistRevision }),
    }),
  /**
   * The visit's work order: sold scope from the quote, plus access notes.
   * The route serializes each task's frequency (commercial specs name it);
   * typed here because the shared `WorksheetTask` predates the field, and
   * optional because a revision snapshot from before it may not carry it.
   */
  worksheet: (id: string) =>
    api.get<
      Omit<VisitWorksheet, 'rooms'> & {
        rooms: Array<
          Omit<WorksheetRoom, 'tasks'> & {
            tasks: Array<WorksheetTask & { frequency?: ServiceFrequency }>;
          }
        >;
      }
    >(`/schedule/appointments/${id}/worksheet`),
  checklist: async (id: string) => {
    try {
      return await api.get<VisitChecklist>(`/schedule/appointments/${id}/checklist`);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 404) {
        return {
          available: false,
          readOnly: true,
          source: 'none',
          progress: { completed: 0, total: 0 },
          items: [],
        } satisfies VisitChecklist;
      }
      throw caught;
    }
  },
  tickChecklistItem: (
    id: string,
    itemId: string,
    body: {
      clientId: string;
      ticked: boolean;
      tickedAt: string;
      status?: 'skipped' | 'not_applicable';
      reason?: string;
      checklistRevision?: number;
    },
  ) =>
    api.post<{ item: VisitChecklist['items'][number]; progress: VisitChecklist['progress']; applied: boolean }>(
      `/schedule/appointments/${id}/checklist/items/${itemId}/tick`,
      body,
    ),
  noteChecklistItem: (
    id: string,
    itemId: string,
    body: { clientId: string; note: string; notedAt: string; checklistRevision?: number },
  ) =>
    api.post<{ item: VisitChecklist['items'][number]; progress: VisitChecklist['progress']; applied: boolean }>(
      `/schedule/appointments/${id}/checklist/items/${itemId}/note`,
      body,
    ),
  attachChecklistPhoto: (
    id: string,
    itemId: string,
    body: { clientId: string; photoId: string; attachedAt: string; checklistRevision?: number },
  ) =>
    api.post<{ item: VisitChecklist['items'][number]; applied: boolean }>(
      `/schedule/appointments/${id}/checklist/items/${itemId}/photo`,
      body,
    ),
  noShow: (id: string) => api.post<Appointment>(`/schedule/appointments/${id}/no-show`, {}),

  /**
   * Quick picks: a few good slots for the crew, ranked by how cleanly a
   * schedule starting there fits. Only the first visit must be clear; later
   * occurrences that clash come back as a count, not a refusal.
   */
  suggestions: (params: {
    userIds?: string[];
    durationMinutes: number;
    frequency: string;
    teamId?: string | null;
    from?: string;
    excludeAppointmentId?: string;
  }) =>
    api.get<{
      timezone: string;
      horizonDays: number;
      /** The visit is longer than the working day, so every pick overruns it. */
      runsPastClosing: boolean;
      suggestions: SlotSuggestion[];
      /**
       * Why the list is empty, when it is — the diary is full, or the search
       * declined to guess. Null whenever there are picks to show.
       */
      emptyReason: SuggestionsEmptyReason | null;
    }>(
      `/schedule/suggestions${queryString({
        durationMinutes: params.durationMinutes,
        frequency: params.frequency,
        ...(params.teamId ? { teamId: params.teamId } : {}),
        ...(params.userIds && params.userIds.length > 0 ? { userIds: params.userIds.join(',') } : {}),
        ...(params.from ? { from: params.from } : {}),
        ...(params.excludeAppointmentId ? { excludeAppointmentId: params.excludeAppointmentId } : {}),
      })}`,
    ),

  listSeries: (params: { customerId?: string; page?: number; pageSize?: number } = {}) =>
    api.get<Paginated<AppointmentSeries>>(`/schedule/series${queryString({ ...params })}`),
  createSeries: (body: Record<string, unknown>, opts?: { isCurrent?: () => boolean }) =>
    withAvailabilityConfirmation(
      (token) =>
        api.post<AppointmentSeries & { skippedVisitDates?: string[] }>('/schedule/series', {
          ...body,
          ...(token ? { availabilityConfirmation: token } : {}),
        }),
      opts?.isCurrent,
    ),
  series: (id: string) =>
    api.get<{
      series: AppointmentSeries;
      upcoming: Appointment[];
      upcomingPagination?: Omit<SeriesAppointmentsPage, 'items'>;
      missingVisitDates: string[];
    }>(`/schedule/series/${id}`),
  seriesAppointments: (
    id: string,
    params: { page: number; pageSize: number; snapshotAt: string; snapshotToken: string },
    signal?: AbortSignal,
  ) => api.get<SeriesAppointmentsPage>(`/schedule/series/${id}/appointments${queryString({ ...params })}`, signal),
  updateSeries: (id: string, body: Record<string, unknown>) =>
    withAvailabilityConfirmation((token) =>
      api.patch<AppointmentSeries>(`/schedule/series/${id}`, {
        ...body,
        ...(token ? { availabilityConfirmation: token } : {}),
      }),
    ),
  previewSeriesHorizon: (id: string, body: SeriesHorizonInput) =>
    api.post<SeriesHorizonPreview>(`/schedule/series/${id}/horizon/preview`, body),
  updateSeriesHorizon: (id: string, body: SeriesHorizonInput & { previewToken?: string }) =>
    api.put<{ series: AppointmentSeries; visitsRemoved: number; visitsAdded: number }>(
      `/schedule/series/${id}/horizon`,
      body,
    ),
  previewRecurringHorizon: (body: { months: 3 | 6 | 9 }) =>
    api.post<RecurringHorizonPreview>('/schedule/recurring-horizon/preview', body),
  recurringHorizon: () =>
    api.get<{ months: 3 | 6 | 9; visitsAdded: number; seriesProcessed: number; backfillPending: boolean }>(
      '/schedule/recurring-horizon',
    ),
  updateRecurringHorizon: (body: { months: 3 | 6 | 9; previewToken?: string }) =>
    api.put<{
      months: 3 | 6 | 9;
      visitsRemoved: number;
      visitsAdded: number;
      seriesProcessed: number;
      backfillPending: boolean;
    }>('/schedule/recurring-horizon', body),
  /**
   * A dated price change on a schedule. `put` because it replaces whatever is
   * pending rather than adding a second one, and `delete` cancels it — which
   * is a different thing from setting the price to nothing.
   */
  scheduleSeriesPriceChange: (
    id: string,
    body: {
      priceCents: number;
      effectiveOn: string;
      notifyCustomer?: boolean;
      dayPrices?: Array<{ dayOfWeek: number; priceCents: number }>;
    },
  ) => api.put<AppointmentSeries>(`/schedule/series/${id}/price-change`, body),
  previewSeriesPriceChange: (
    id: string,
    body: {
      priceCents: number;
      effectiveOn: string;
      notifyCustomer?: boolean;
      dayPrices?: Array<{ dayOfWeek: number; priceCents: number }>;
    },
  ) =>
    api.post<{ recipient: string; subject: string; body: string } | { recipient: null }>(
      `/schedule/series/${id}/price-change/preview`,
      body,
    ),
  cancelSeriesPriceChange: (id: string) => api.delete<AppointmentSeries>(`/schedule/series/${id}/price-change`),
  pauseSeries: (id: string) => api.post<AppointmentSeries>(`/schedule/series/${id}/pause`, {}),
  resumeSeries: (id: string) => api.post<AppointmentSeries>(`/schedule/series/${id}/resume`, {}),
  endSeries: (id: string) => api.post<AppointmentSeries>(`/schedule/series/${id}/end`, {}),

  /**
   * The customer page's schedule panel: their recurring schedules (paged) plus
   * a short look-ahead of upcoming visits (capped server-side, not paged —
   * it is a window, not an archive).
   */
  customerSchedule: (customerId: string, params: { page?: number; pageSize?: number } = {}) =>
    api.get<{
      series: AppointmentSeries[];
      seriesPage: number;
      seriesPageSize: number;
      seriesTotal: number;
      upcoming: Appointment[];
    }>(`/schedule/customers/${customerId}${queryString({ ...params })}`),

  feeds: () =>
    fetchAllPages<CalendarFeed>((page, pageSize) =>
      api.get<Paginated<CalendarFeed>>(`/schedule/feeds${queryString({ page, pageSize })}`),
    ),
  createFeed: (body: { label?: string; userId?: string | null }) =>
    api.post<{ feed: CalendarFeed; url: string }>('/schedule/feeds', body),
  revokeFeed: (id: string) => api.delete<void>(`/schedule/feeds/${id}`),
};

// --- Payments -----------------------------------------------------------------

export interface PaymentsStatus {
  configured: boolean;
  provider: string | null;
  webhookConfigured: boolean;
  /** The fee added to online card payments, in basis points. 0 = none. */
  cardFeeBasisPoints: number;
  /**
   * Which account the session's current location resolves to: its own
   * (`location`) or the company-wide one it inherits (`company`).
   */
  source: 'company' | 'location';
  /**
   * Whether the session's current location offers tips, and the preset
   * percentages (basis points) to offer beside a custom amount. Answered even
   * with no processor connected — a cash tip needs none.
   */
  tips: { enabled: boolean; presetBasisPoints: number[] };
}

/** Whose Stripe account a settings read or write is about. */
export type PaymentSettingsScope = 'company' | 'location';

/**
 * A settings response: the stored shape plus which scope answered and the
 * URL path this scope's Stripe dashboard must register as its webhook
 * endpoint. Intersection typing for the same reason as `LocationWithIntakeLink`.
 */
export type PaymentSettingsResponse = PaymentSettings & {
  scope: PaymentSettingsScope;
  /**
   * Register `window.location.origin + webhookPath` in that account's Stripe
   * dashboard. Optional because the worker may instead resolve the absolute
   * per-scope `webhookUrl` itself — the UI uses whichever the response carries.
   */
  webhookPath?: string;
};

export const paymentApi = {
  status: () => api.get<PaymentsStatus>('/payments/status'),
  /**
   * Without `locationId`: the company account. With it: that location's own
   * account — the not-configured shape when the location has none and inherits
   * the company's.
   */
  settings: (locationId?: string) =>
    api.get<PaymentSettingsResponse>(`/payments/settings${queryString({ locationId })}`),
  /** With `locationId` in the body, writes that location's own account. */
  saveSettings: (body: {
    provider?: PaymentProviderId;
    /** The provider's primary secret. Which one that is depends on the provider. */
    secretKey?: string;
    /** Its remaining fields, keyed by the names its descriptor declares. */
    credentials?: Record<string, string>;
    webhookSecret?: string;
    /** Test or live, for the providers whose credentials do not say. */
    environment?: 'test' | 'live';
    enabled?: boolean;
    cardFeeEnabled?: boolean;
    cardFeeBasisPoints?: number;
    locationId?: string;
  }) => api.patch<PaymentSettingsResponse>('/payments/settings', body),
  /** With `locationId`, removes that location's own account — it goes back to inheriting the company's. */
  /**
   * Begins Stripe Connect: answers the stripe.com authorize URL the browser
   * should leave for. The callback lands back on Settings with a
   * ?stripeConnect= outcome flag.
   */
  connectStart: (locationId?: string, options: { returnPath?: string } = {}) =>
    api.post<{ url: string }>('/payments/settings/connect/start', {
      ...(locationId ? { locationId } : {}),
      ...(options.returnPath ? { returnPath: options.returnPath } : {}),
    }),
  disconnect: (locationId?: string) =>
    api.delete<PaymentSettingsResponse>(`/payments/settings${queryString({ locationId })}`),
  /**
   * Mints a hosted card-setup link. `email` and `text` are independent: either,
   * both, or neither — with neither, the URL comes back for the office to hand
   * over itself.
   */
  setupLink: (customerId: string, delivery: { email?: boolean; text?: boolean; recipientPhone?: string } = {}) =>
    api.post<{
      url: string;
      emailed: boolean;
      texted: boolean;
      textDeferred: boolean;
      /** The fee stamped on THIS session — what the saved card will agree to. */
      cardFeeBasisPoints: number;
    }>(`/payments/customers/${customerId}/setup-link`, delivery),
  removeCard: (customerId: string) => api.delete<{ removed: boolean }>(`/payments/customers/${customerId}/card`),
  /**
   * `appointmentId` prices `cardFeeBasisPoints` at that visit's location, where
   * a charge for it (its late fee) is billed, instead of the customer's own.
   */
  customerPayments: (customerId: string, params: { page?: number; pageSize?: number; appointmentId?: string } = {}) =>
    api.get<Paginated<Payment> & { cardFeeBasisPoints: number }>(
      `/payments/customers/${customerId}${queryString(params)}`,
    ),
  /** One quote's ledger and where its money stands (0036_quote_payments.sql). */
  quotePayments: (quoteId: string) => api.get<QuotePaymentLedger>(`/payments/quotes/${quoteId}`),
  /**
   * What is still owed on accepted one-time quotes: the location's (every
   * location the viewer has, with `scope: 'all'`), or one customer's.
   */
  owed: (customerId?: string, scope?: 'all') =>
    api.get<OwedOnQuotes>(`/payments/owed${queryString({ customerId, scope })}`),
  appointmentPayments: (appointmentId: string) =>
    api.get<{
      items: Payment[];
      cardFeeBasisPoints: number;
      refundableCents: number;
      cardOnFile: boolean;
      /** The saved card a charge would take. Absent from servers before it was added. */
      card?: { brand: string | null; last4: string | null } | null;
      /** Paid on the quote this visit booked, before it was booked: charging the visit takes the rest. */
      paidOnQuoteCents: number;
    }>(`/payments/appointments/${appointmentId}`),
  chargeAppointment: (
    appointmentId: string,
    body: {
      amountCents?: number;
      expectedAmountCents?: number;
      expectedTotalCents?: number;
      expectedCardLast4?: string;
      expectedCardBrand?: string;
      description?: string;
    },
  ) => api.post<Payment>(`/payments/appointments/${appointmentId}/charge`, body),
  /** A manual invoice's own ledger (0103) — the rows carrying its id. */
  invoicePayments: (invoiceId: string) =>
    api.get<{
      items: Payment[];
      cardFeeBasisPoints: number;
      refundableCents: number;
      cardOnFile: boolean;
      balanceCents: number;
      /** What is still owed back on a credit memo, counting settled refunds only. */
      creditRemainingCents: number;
      /**
       * A credit memo's refundable-now figure — what this panel's form may
       * send, refunds already in flight reserved. NOT the memo's "still owed
       * back", which is the field above (and `totals.creditRemainingCents` on
       * the invoice), which is why the two are named apart.
       */
      creditRefundableNowCents: number;
    }>(`/payments/invoices/${invoiceId}`),
  /** The card on file, charged against a manual invoice's remaining balance. */
  chargeInvoice: (invoiceId: string, body: { amountCents?: number; description?: string }) =>
    api.post<Payment>(`/payments/invoices/${invoiceId}/charge`, body),
  /** The same payment link the visit's request sends, for a manual invoice. */
  requestInvoicePayment: (
    invoiceId: string,
    body: {
      amountCents?: number;
      description?: string;
      sendEmail?: boolean;
      sendText?: boolean;
    },
  ) =>
    api.post<{
      payment: Payment;
      url: string;
      emailedTo: string | null;
      textedTo: string | null;
      textDeferredUntilOpenHours: boolean;
      textFailed: boolean;
    }>(`/payments/invoices/${invoiceId}/request-payment`, body),
  /**
   * Settles a credit memo: money back to the customer, either on one of their
   * past card payments (`paymentId`) or by hand through one of the company's
   * own methods (`methodId`). Exactly one of the two.
   */
  refundCredit: (
    invoiceId: string,
    body: { paymentId?: string; methodId?: string; amountCents?: number; reason?: string; receivedOn?: string },
  ) => api.post<Payment>(`/payments/invoices/${invoiceId}/refund`, body),
  /**
   * Sends the customer a payment link. `sendEmail` defaults on server-side and
   * `sendText` off, so a caller that says nothing behaves as it always has.
   */
  requestPayment: (
    appointmentId: string,
    body: {
      amountCents?: number;
      description?: string;
      sendEmail?: boolean;
      sendText?: boolean;
    },
  ) =>
    api.post<{
      payment: Payment;
      url: string;
      emailedTo: string | null;
      textedTo: string | null;
      textDeferredUntilOpenHours: boolean;
      textFailed: boolean;
    }>(`/payments/appointments/${appointmentId}/request-payment`, body),
  createCharge: (body: {
    customerId: string;
    appointmentId?: string | null;
    amountCents: number;
    description?: string;
  }) => api.post<Payment>('/payments/charges', body),
  refund: (paymentId: string, body: { amountCents?: number; reason?: string }) =>
    api.post<Payment>(`/payments/${paymentId}/refund`, body),
  /**
   * Asks the processor what became of a refund it never confirmed, and settles
   * the ledger row from the answer. Moves no money of its own.
   */
  recheckRefund: (paymentId: string) => api.post<Payment>(`/payments/${paymentId}/recheck`, {}),
  /**
   * The URL only — the bytes are pulled by `PdfDownloadButton` through
   * `apiDownload`, on the session cookie the browser already holds, so the
   * on-demand receipt render has an indicator behind it while it runs.
   */
  receiptUrl: (paymentId: string) => `/api/v1/payments/${paymentId}/receipt`,
  resendReceipt: (paymentId: string) => api.post<{ resentTo: string }>(`/payments/${paymentId}/receipt/resend`, {}),
  // The company's own payment methods — how a manual payment is recorded.
  // Deliberately independent of `configured`: they work without a provider.
  listMethods: (includeInactive = false) =>
    api.get<{ items: CompanyPaymentMethod[] }>(`/payments/methods${includeInactive ? '?includeInactive=true' : ''}`),
  createMethod: (body: {
    name: string;
    nameTranslations?: LocalizedText | null;
    textLanguage?: SupportedLanguage;
    family?: PaymentMethodFamily;
  }) => api.post<CompanyPaymentMethod>('/payments/methods', body),
  updateMethod: (
    methodId: string,
    body: {
      name?: string;
      nameTranslations?: LocalizedText | null;
      family?: PaymentMethodFamily;
      isActive?: boolean;
    },
  ) => api.patch<CompanyPaymentMethod>(`/payments/methods/${methodId}`, body),
  deactivateMethod: (methodId: string) => api.delete<CompanyPaymentMethod>(`/payments/methods/${methodId}`),
  recordPayment: (body: {
    customerId: string;
    appointmentId?: string | null;
    /** The manual invoice this money settles (0103). Never beside an appointment. */
    invoiceId?: string | null;
    /** The accepted quote this money was paid on. Never beside a visit or an invoice. */
    quoteId?: string | null;
    amountCents?: number;
    /**
     * The quote's balance `amountCents` was filled in from; refused (409)
     * once the quote no longer owes exactly that.
     */
    expectedBalanceCents?: number;
    /** A tip received on top of `amountCents`; needs tips switched on. */
    tipCents?: number;
    methodId: string;
    description?: string;
    receivedOn?: string;
    /**
     * Money handed over in another currency (0115), instead of `amountCents`:
     * converted at `exchangeRate`, or at the company's kept rate without one.
     */
    received?: { currencyCode: string; amountCents: number; exchangeRate?: string };
  }) => api.post<Payment>('/payments/record', body),
};

// --- Quotes -----------------------------------------------------------------

/**
 * One job at a time from an approved quote (`single_job`, every plan): book
 * it, change or cancel it, mark it completed, get paid, book it again, and
 * the Booked list. See routes/single-job.ts.
 */
export const singleJobApi = {
  get: (quoteId: string) => api.get<SingleJob>(`/quotes/${quoteId}/job`),
  book: (quoteId: string, body: { startsOn: string; startTimeLocal?: string; durationMinutes?: number }) =>
    api.post<SingleJobActionResult>(`/quotes/${quoteId}/job`, body),
  /** `durationMinutes: null` puts the length back to the quote's estimate. */
  reschedule: (
    quoteId: string,
    body: { scheduledDate?: string; startTimeLocal?: string; durationMinutes?: number | null },
  ) => api.patch<SingleJobActionResult>(`/quotes/${quoteId}/job`, body),
  cancel: (quoteId: string, reason?: string) =>
    api.post<SingleJobActionResult>(`/quotes/${quoteId}/job/cancel`, reason ? { reason } : {}),
  complete: (quoteId: string, completionOverrideReason?: string, checklistRevision?: number) =>
    api.post<SingleJob>(`/quotes/${quoteId}/job/complete`, {
      ...(completionOverrideReason ? { completionOverrideReason } : {}),
      ...(checklistRevision === undefined ? {} : { checklistRevision }),
    }),
  /** `email: false` makes the link without sending it, to be copied and shared by hand. */
  paymentLink: (quoteId: string, email: boolean) =>
    api.post<{
      url: string;
      emailedTo: string | null;
      /** The address the link was meant to be emailed to, when that email did not go out. */
      notSentTo: string | null;
      job: SingleJob;
    }>(`/quotes/${quoteId}/job/payment-link`, {
      email,
    }),
  /** The whole balance, by cash, check or another type. */
  recordPayment: (quoteId: string, body: { methodId: string; description?: string; receivedOn?: string }) =>
    api.post<SingleJob>(`/quotes/${quoteId}/job/record-payment`, body),
  bookAgain: (quoteId: string) => api.post<Quote>(`/quotes/${quoteId}/book-again`, {}),
  booked: (params: { page?: number; pageSize?: number } = {}) =>
    api.get<Paginated<BookedJob>>(`/quotes/booked${queryString({ ...params })}`),
};

export const quoteApi = {
  reviewWalkthrough: (id: string, version: number, assessmentKey: string) =>
    api.post<Quote>(`/quotes/${id}/walkthrough-review`, { version, assessmentKey, confirmVisited: true }),
  list: (params: {
    page?: number;
    pageSize?: number;
    q?: string;
    status?: string;
    customerId?: string;
    expiringWithinDays?: number;
    /** Only the signed-in person's own quotes. */
    mine?: '1';
    sort?: string;
  }) => api.get<Paginated<QuoteSummary>>(`/quotes${queryString({ ...params })}`),
  /** Cancels what it can, reports what it refused — partial success expected. */
  bulkCancel: (ids: string[]) =>
    api.post<{
      cancelledIds: string[];
      skipped: Array<{ id: string; reason: string }>;
    }>('/quotes/bulk-cancel', {
      ids,
    }),
  get: (id: string, signal?: AbortSignal) => api.get<Quote>(`/quotes/${id}`, signal),
  /** Never accepts an IndexedDB fallback: callers use this before discarding local work. */
  getFresh: (id: string, signal?: AbortSignal) => api.getFresh<Quote>(`/quotes/${id}`, signal),
  /**
   * `segment` is only worth sending from a company that does both kinds of
   * work — anywhere else the Worker's default (the company's own mode) is
   * already the right answer, and the quote's segment is fixed at creation.
   */
  create: (body: { customerId: string; propertyId?: string | null; segment?: QuoteSegment }) =>
    api.post<Quote>('/quotes', body),
  update: (id: string, version: number, changes: QuoteChangesInput) =>
    api.patch<Quote>(`/quotes/${id}`, { version, changes }),
  remove: (id: string) => api.delete<void>(`/quotes/${id}`),
  /**
   * Copies a quote into a new draft. `customerId` and `propertyId` are what
   * makes it a clone rather than a duplicate: the same rooms, tasks, fees and
   * terms, quoted for somebody else. Both are optional — left out, the copy
   * keeps the source's own customer and address.
   */
  duplicate: (id: string, body: { customerId?: string; propertyId?: string | null } = {}) =>
    api.post<Quote>(`/quotes/${id}/duplicate`, body),
  recalculate: (id: string, version: number) => api.post<Quote>(`/quotes/${id}/recalculate`, { version }),
  markReady: (id: string, version: number) => api.post<Quote>(`/quotes/${id}/ready`, { version }),
  publish: (id: string, version: number, regenerateToken = false) =>
    api.post<{ quote: Quote; publicUrl: string | null }>(`/quotes/${id}/publish`, { version, regenerateToken }),
  unpublish: (id: string, version: number) => api.post<Quote>(`/quotes/${id}/unpublish`, { version }),
  send: (id: string, version: number, recipientEmail?: string, message?: string) =>
    api.post<{
      quote: Quote;
      queuedFor: string;
      publicUrl: string | null;
      delivered: boolean;
    }>(`/quotes/${id}/send`, {
      version,
      recipientEmail,
      message,
    }),
  /**
   * The same send by text. `noteTruncated` and `segments` are what the email
   * response has no equivalent of: a text is billed by the segment and the note
   * is trimmed to fit, so both belong in front of the sender afterwards.
   */
  sendText: (id: string, version: number, recipientPhone?: string, message?: string) =>
    api.post<{
      quote: Quote;
      queuedFor: string;
      publicUrl: string | null;
      segments: number;
      noteTruncated: boolean;
      delivered: boolean;
      unconfirmed?: boolean;
      failureReason?: string | null;
      deferredUntilOpenHours: boolean;
    }>(`/quotes/${id}/send-text`, { version, recipientPhone, message }),
  /**
   * Email and text together, sharing one share link. One request so the
   * customer gets both messages from a single send, instead of two calls that
   * each restamp the quote and can lose each other's version. A live link is
   * reused; it is not replaced.
   */
  sendBoth: (id: string, version: number, recipientEmail?: string, recipientPhone?: string, message?: string) =>
    api.post<{
      quote: Quote;
      emailQueuedFor: string;
      emailDelivered: boolean;
      textQueuedFor: string;
      publicUrl: string | null;
      segments: number;
      noteTruncated: boolean;
      delivered: boolean;
      unconfirmed?: boolean;
      failureReason?: string | null;
      deferredUntilOpenHours: boolean;
    }>(`/quotes/${id}/send-both`, {
      version,
      recipientEmail,
      recipientPhone,
      message,
    }),
  /**
   * `allowUnsent` records a yes the customer gave off-app, so a quote that
   * never went out can still be accepted (see `STAFF_ACCEPTABLE_QUOTE_STATUSES`).
   */
  accept: (
    id: string,
    version: number,
    allowUnsent = false,
    frequency?: SeriesFrequency,
    firstCleanOptionId?: string,
  ) =>
    api.post<Quote>(`/quotes/${id}/accept`, {
      version,
      allowUnsent,
      ...(frequency ? { frequency } : {}),
      ...(firstCleanOptionId ? { firstCleanOptionId } : {}),
    }),
  /** Puts an accepted quote on the calendar. A second call returns the visit already booked. */
  book: (id: string, input: { startsOn: string; startTimeLocal?: string }) =>
    api.post<{ seriesId: string; startsOn: string; startTimeLocal: string; alreadyBooked: boolean }>(
      `/quotes/${id}/book`,
      input,
    ),
  /**
   * Accept and book in one call. A quote already on the calendar answers 409
   * `ALREADY_SCHEDULED`. A taken day answers 409 `VISIT_DAY_TAKEN` and the
   * quote stays accepted. A quote that is already accepted is booked and not
   * accepted again.
   */
  acceptAndBook: (
    id: string,
    input: {
      version: number;
      firstCleanOptionId?: string;
      allowUnsent?: boolean;
      frequency?: SeriesFrequency;
      startsOn: string;
      startTimeLocal?: string;
    },
  ) =>
    api.post<{
      quote: Quote;
      seriesId: string;
      startsOn: string;
      startTimeLocal: string;
      alreadyBooked: boolean;
    }>(`/quotes/${id}/accept-and-book`, input),
  decline: (id: string, version: number) => api.post<Quote>(`/quotes/${id}/decline`, { version }),
  complete: (id: string, version: number) => api.post<Quote>(`/quotes/${id}/complete`, { version }),
  cancel: (id: string, version: number) => api.post<Quote>(`/quotes/${id}/cancel`, { version }),
  /** `declineReason` is the countable "why", and only means anything when status is `declined`. */
  overrideStatus: (id: string, version: number, status: string, reason: string, declineReason?: string) =>
    api.post<Quote>(`/quotes/${id}/status`, {
      version,
      status,
      reason,
      declineReason: declineReason || undefined,
    }),
  generatePdf: (id: string) =>
    api.post<{
      downloadUrl: string;
      generatedAt: string;
      sizeBytes: number;
      version: number;
    }>(`/quotes/${id}/generate-pdf`, {}),
  /**
   * The URL only, same as `invoiceApi.pdfUrl`: `PdfDownloadButton` fetches it
   * so the render has a visible wait, rather than navigating to it. 404s until
   * `generatePdf` has run at least once for this quote; 409s if the quote has
   * changed since, unless `allowStale` — the editor's own download button
   * passes that, since it shows its own "out of date, regenerate?" warning
   * next to the link instead of relying on the server's refusal.
   */
  pdfUrl: (id: string, options?: { allowStale?: boolean }) =>
    `/api/v1/quotes/${id}/pdf${options?.allowStale ? '?allowStale=1' : ''}`,
  /**
   * The pay terms and settings the quote screen estimates profit from (0035).
   * Pro, owner and managers; the estimate itself runs in the browser.
   */
  profitInputs: (id: string) => api.get<QuoteProfitInputs>(`/quotes/${id}/profit-inputs`),
  history: (id: string) =>
    api.get<{
      events: Array<{
        id: string;
        action: string;
        summary: string | null;
        createdAt: string;
      }>;
      revisions: Array<{
        id: string;
        revisionNumber: number;
        statusAtRevision: string;
        createdAt: string;
      }>;
    }>(`/quotes/${id}/history`),
};

// --- Invoices -----------------------------------------------------------------

export const invoiceApi = {
  list: (params: {
    page?: number;
    pageSize?: number;
    q?: string;
    status?: string;
    kind?: 'visit' | 'manual';
    customerId?: string;
    from?: string;
    to?: string;
    window?: 'current' | 'upcoming';
    sort?: string;
  }) => api.get<Paginated<Invoice>>(`/invoices${queryString({ ...params })}`),
  /** One invoice with the ledger of the visit it bills, and what that leaves owing. */
  get: (id: string, signal?: AbortSignal) => api.get<InvoiceDetail>(`/invoices/${id}`, signal),
  /**
   * Writes an invoice by hand, out of signed line items (0103): a late fee,
   * equipment sold, the cleaning nobody scheduled — or, with negative lines, a
   * credit memo for money the company owes back.
   */
  create: (body: {
    customerId: string;
    lineItems: Array<{ description: string; quantity: number; unitCents: number }>;
    issuedOn?: string;
    poNumber?: string;
  }) => api.post<InvoiceDetail>('/invoices', body),
  /** Unwrites a manual invoice — refused once money has moved against it. */
  void: (id: string) => api.post<Invoice>(`/invoices/${id}/void`, {}),
  /**
   * The URL only, like the payment receipt above: `PdfDownloadButton` fetches
   * it so the render has a visible wait, rather than navigating to it.
   */
  pdfUrl: (id: string) => `/api/v1/invoices/${id}/pdf`,
  /**
   * Emails the customer their invoice on demand — the office's own copy of the
   * send that otherwise happens once, automatically, when the visit starts.
   * Omit `recipientEmail` to use the address on file; passing one addresses
   * this message only and leaves the customer record alone.
   *
   * `delivered` is the provider's answer, not the intent: a suppressed or
   * bounced address answers 200 with `false`, because the send did happen and
   * the office needs to know it went nowhere.
   */
  send: (id: string, body: { recipientEmail?: string } = {}) =>
    api.post<{ emailedTo: string; delivered: boolean }>(`/invoices/${id}/send`, body),
  /**
   * The same send over a page of ticked rows. Never fails as a whole: an
   * invoice the endpoint will not send (canceled, settled, no address) comes
   * back as its own `skipped` result with a reason, so twelve invoices in
   * twelve different states still send the ones that can go.
   */
  sendBatch: (invoiceIds: string[]) => api.post<InvoiceBatchSendResult>('/invoices/send-batch', { invoiceIds }),
  /**
   * Consolidated monthly invoices — one document per customer per month, for
   * customers on consolidated billing. A separate list from `list` because
   * the two are different animals: a monthly invoice has a period and a
   * snapshotted total where a visit invoice has a visit.
   */
  monthlyList: (page = 1) =>
    api.get<{
      items: MonthlyInvoice[];
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    }>(`/invoices/monthly?page=${page}`),
  /** One monthly invoice with the visits it covers — money settles per visit. */
  /**
   * One monthly invoice with everything it charged for — the visits, the
   * approved change orders and the delivered supplies. All three, because the
   * total is the sum of all three and a breakdown that omits any of them is a
   * document nobody can reconcile.
   */
  monthlyGet: (id: string, signal?: AbortSignal) => api.get<MonthlyInvoiceDetail>(`/invoices/monthly/${id}`, signal),
};

/**
 * Extra work (change orders, migration 0088; on a visit since 0104): agreed
 * once, then added to one of the customer's visits or, with no visit,
 * appended to their monthly invoice.
 */
export const changeOrderApi = {
  list: (params: { page?: number; pageSize?: number; status?: string; customerId?: string; appointmentId?: string }) =>
    api.get<{
      items: ChangeOrder[];
      page: number;
      pageSize: number;
      total: number;
    }>(`/change-orders${queryString({ ...params })}`),
  get: (id: string, signal?: AbortSignal) => api.get<ChangeOrder>(`/change-orders/${id}`, signal),
  /** The customer's upcoming visits extra work could be added to, and why any cannot. */
  visitOptions: (customerId: string) =>
    api.get<{ items: ExtraWorkVisitOption[] }>(`/change-orders/visit-options${queryString({ customerId })}`),
  create: (body: {
    customerId: string;
    propertyId?: string | null;
    appointmentId?: string | null;
    addOnId?: string | null;
    title: string;
    description?: string | null;
    amountCents: number;
    isTaxable?: boolean;
    serviceDate?: string | null;
    /** The customer already said yes: approve it now and, on a visit, put it there. */
    alreadyAgreed?: boolean;
    agreedBy?: string | null;
    /** One per submission, kept across its retries: a retry gets the same order back. */
    requestId?: string;
  }) => api.post<ChangeOrderResult>('/change-orders', body),
  update: (id: string, body: Record<string, unknown>) => api.patch<ChangeOrder>(`/change-orders/${id}`, body),
  /**
   * Asks the client. The approval URL comes back HERE and nowhere else —
   * only its hash is stored — so the office can paste it into their own
   * email. Re-sending mints a fresh link and retires the previous one.
   */
  send: (id: string, body: { email?: string } = {}) =>
    api.post<ChangeOrderSendResult>(`/change-orders/${id}/send`, body),
  cancel: (id: string) => api.post<ChangeOrder>(`/change-orders/${id}/cancel`, {}),
  /** The office recording that the customer agreed, instead of waiting on the link. */
  agree: (id: string, body: { agreedBy?: string | null } = {}) =>
    api.post<ChangeOrderResult>(`/change-orders/${id}/agree`, body),
  /** Tries again to put approved work onto a visit it could not go onto before. */
  addToVisit: (id: string) => api.post<ChangeOrderResult>(`/change-orders/${id}/add-to-visit`, {}),
};

// --- Quality inspections ----------------------------------------------------

/** One scored line as the form holds it, before it is sent. */
export interface InspectionItemInput {
  id?: string;
  areaName: string;
  itemName: string;
  /** Null is "could not be checked", which is not a zero and is not scored. */
  rating?: number | null;
  comment?: string | null;
  /** A photograph already uploaded against this walkthrough. */
  photoFileId?: string | null;
}

export const inspectionApi = {
  list: (
    params: {
      status?: string;
      customerId?: string;
      propertyId?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) => api.get<Paginated<Inspection>>(`/inspections${queryString({ ...params })}`),
  get: (id: string, signal?: AbortSignal) => api.get<Inspection>(`/inspections/${id}`, signal),
  /** Never falls back to IndexedDB: queued-write conflict detection needs proof from the server. */
  getFresh: (id: string, signal?: AbortSignal) => api.getFresh<Inspection>(`/inspections/${id}`, signal),
  create: (body: {
    propertyId: string;
    scheduledFor: string;
    inspectorUserId?: string | null;
    /** What the walk will check, listed before anybody walks it. */
    items?: Array<{ areaName: string; itemName: string }>;
  }) => api.post<Inspection>('/inspections', body),
  /**
   * The lines are sent whole rather than patched one at a time — the server
   * makes the stored list match, like the quote editor's rooms. A supervisor
   * adds, removes and re-orders as they walk, and reconciling that line by
   * line over a patchy connection is how a form loses somebody's work.
   */
  update: (
    id: string,
    body: {
      /** Compare-and-swap token returned by the last inspection read. */
      version: number;
      scheduledFor?: string;
      inspectorUserId?: string | null;
      internalNotes?: string | null;
      customerNotes?: string | null;
      sharedWithClient?: boolean;
      items?: InspectionItemInput[];
    },
  ) => api.patch<Inspection>(`/inspections/${id}`, body),
  /** Closes the walkthrough and snapshots its score. Refused twice over. */
  complete: (id: string) => api.post<Inspection>(`/inspections/${id}/complete`, {}),
  /**
   * The work list, capped server-side. `total` is what the page needs to say
   * when the cap has hidden something — an outstanding-work list that reads
   * as complete when it is not is the one failure that matters here.
   */
  openActions: () => api.get<{ items: OpenInspectionAction[]; total: number }>('/inspections/actions/open'),
  raiseAction: (
    id: string,
    body: {
      description: string;
      dueOn?: string | null;
      inspectionItemId?: string | null;
      /** Who owns the fix. Null is an action nobody has picked up yet. */
      assignedUserId?: string | null;
    },
  ) => api.post<InspectionAction>(`/inspections/${id}/actions`, body),
  resolveAction: (actionId: string, body: { resolutionNote?: string | null } = {}) =>
    api.post<{ id: string; resolved: boolean }>(`/inspections/actions/${actionId}/resolve`, body),
};

/** One visit on a monthly invoice's own detail — where its money actually settles. */
export interface MonthlyInvoiceVisit {
  appointmentId: string;
  scheduledDate: string;
  priceCents: number | null;
  currencyCode: string;
  paymentStatus: AppointmentPaymentStatus;
  status: AppointmentStatus;
  propertyAddress: string | null;
}

export const billingApi = {
  /** The app subscription (trial, seats, plan) — not the company's own gateway. */
  get: () => api.get<BillingState>('/billing'),
  /** Both return a Stripe-hosted URL the browser should navigate to. */
  checkout: (plan: PaidPlanId, interval: BillingInterval = 'month') =>
    api.post<{ url: string }>('/billing/checkout', { plan, interval }),
  portal: () => api.post<{ url: string }>('/billing/portal', {}),
  /**
   * Moves a subscribed company between plans in place, prorated. Omitting
   * the interval keeps the cycle the company is already on.
   */
  changePlan: (plan: PaidPlanId, interval?: BillingInterval) =>
    api.post<BillingState>('/billing/change-plan', interval ? { plan, interval } : { plan }),
  /** How many active locations a downgrade to `plan` would lock. */
  downgradeImpact: (plan: PlanId) =>
    api.get<{ lockedLocations: number }>(`/billing/downgrade-impact${queryString({ plan })}`),
  /**
   * Moves the company onto the Free plan. No gateway, no card, no URL to
   * follow — the answer is the new billing state, which is why this is a
   * sibling of changePlan rather than of checkout.
   */
  chooseFree: () => api.post<BillingState>('/billing/free', {}),
};

// --- Quote requests (leads from the public intake form) ----------------------

export const quoteRequestApi = {
  list: (params: { page?: number; pageSize?: number; status?: string; source?: string; photoFilter?: string }) =>
    api.get<Paginated<QuoteRequest>>(`/quote-requests${queryString({ ...params })}`),
  newCount: () => api.get<{ count: number }>('/quote-requests/new-count'),
  dismiss: (id: string) => api.post<QuoteRequest>(`/quote-requests/${id}/dismiss`, {}),
  convert: (id: string) =>
    api.post<{
      request: QuoteRequest;
      customerId: string;
      quoteId: string;
      aiDraftApplied: boolean;
    }>(`/quote-requests/${id}/convert`, {}),
  photos: (id: string) =>
    api.get<{
      photos: Array<{ id: string; contentType: string; sizeBytes: number }>;
    }>(`/quote-requests/${id}/photos`),
  /** Same-origin image URL for a lead photo (Worker-proxied, session-authed). */
  photoUrl: (id: string, photoId: string) => `${API_BASE}/quote-requests/${id}/photos/${photoId}`,
};

// --- Photo quotes (WILD-05) ----------------------------------------------------

export const photoQuoteApi = {
  list: (params: { filter?: PhotoIntakeFilter; page?: number; pageSize?: number }) =>
    api.get<Paginated<PhotoIntakeSummary>>(`/photo-intakes${queryString({ ...params })}`),
  count: () => api.get<{ count: number }>('/photo-intakes/count'),
  get: (id: string) => api.get<PhotoIntakeDetail>(`/photo-intakes/${id}`),
  editFinding: (id: string, findingId: string, payload: { version: number; value: Record<string, unknown> }) =>
    api.patch<PhotoIntakeDetail>(`/photo-intakes/${id}/findings/${findingId}`, payload),
  addRoom: (id: string, payload: { version: number; value: RoomFindingValue }) =>
    api.post<PhotoIntakeDetail>(`/photo-intakes/${id}/rooms`, payload),
  send: (
    id: string,
    payload: {
      version: number;
      kind: PhotoQuotePriceKind;
      recipientEmail?: string;
      message?: string;
      confirmHourlyPricing?: boolean;
    },
  ) =>
    api.post<{ intake: PhotoIntakeDetail; quoteId: string; publicUrl: string; emailed: boolean }>(
      `/photo-intakes/${id}/send`,
      payload,
    ),
  askMore: (id: string, payload: { needed: PhotoRequestNeed[]; message?: string }) =>
    api.post<PhotoIntakeDetail>(`/photo-intakes/${id}/ask-more`, payload),
  walkthrough: (id: string, payload: { version: number; message?: string }) =>
    api.post<PhotoIntakeDetail>(`/photo-intakes/${id}/walkthrough`, payload),
  analyze: (id: string, version: number) => api.post<PhotoIntakeDetail>(`/photo-intakes/${id}/analyze`, { version }),
  settings: () => api.get<PhotoQuoteSettingsView>('/photo-intakes/settings'),
  updateSettings: (patch: PhotoQuoteSettingsPatch) =>
    api.patch<PhotoQuoteSettingsView>('/photo-intakes/settings', patch),
  demo: (language?: SupportedLanguage) => api.get<PhotoQuoteDemo>(`/photo-intakes/demo${queryString({ language })}`),
  /** Same-origin, session-authed: the Worker proxies the bytes like every stored object. */
  photoUrl: (id: string, photoId: string) => `${API_BASE}/photo-intakes/${id}/photos/${photoId}`,
};

// --- AI assists ---------------------------------------------------------------

export const aiApi = {
  usage: () => api.get<AiUsageSummary>('/ai/usage'),
  suggestions: (params: { page?: number; pageSize?: number; status?: string }) =>
    api.get<Paginated<AiSuggestion>>(`/ai/suggestions${queryString({ ...params })}`),
  analyze: () => api.post<{ created: number; reason?: string }>('/ai/suggestions/analyze', {}),
  acceptSuggestion: (id: string) => api.post<AiSuggestion>(`/ai/suggestions/${id}/accept`, {}),
  dismissSuggestion: (id: string) => api.post<AiSuggestion>(`/ai/suggestions/${id}/dismiss`, {}),
  reviewReply: (payload: { reviewText: string; rating?: number | null }) =>
    api.post<{ reply: string }>('/ai/review-reply', payload),
};

export const inboundTextApi = {
  list: (params: { page?: number; pageSize?: number; status?: string }) =>
    api.get<Paginated<InboundText>>(`/inbound-texts${queryString({ ...params })}`),
  handle: (id: string) => api.post<InboundText>(`/inbound-texts/${id}/handle`, {}),
  dismiss: (id: string) => api.post<InboundText>(`/inbound-texts/${id}/dismiss`, {}),
};

// --- Management views (reports:read) -----------------------------------------

export const dashboardApi = {
  /** Default scope is the session's current location; 'all' spans every assigned one. */
  get: (scope?: 'all') => api.get<DashboardData>(`/dashboard${queryString({ scope })}`),
};

/** The estimator's landing page (quotes:read): their quotes and the location's. */
export const salesDashboardApi = {
  get: () => api.get<SalesDashboardData>('/sales-dashboard'),
};

/**
 * The reports pages, behind the same `reports:read` + `reports` plan feature
 * as the dashboard. Range-driven reports take inclusive company-local `from`/
 * `to` dates; the others are snapshots and ignore the range. `scope: 'all'`
 * widens from the working location to every location the viewer may report on.
 */
export interface ReportRangeParams {
  pages?: string;
  from?: string;
  to?: string;
  scope?: 'all';
}

export const reportsApi = {
  operational: (report: OperationalReportId, params: ReportRangeParams) =>
    api.get<OperationalReport>(`/reports/${report}${queryString({ ...params })}`),
  profitability: ({ from, to, scope, pages }: ReportRangeParams = {}) =>
    api.get<ProfitabilityReport>(`/reports/profitability${queryString({ from, to, scope, pages })}`),
  retention: (scope?: 'all', pages?: string) =>
    api.get<RetentionReport>(`/reports/retention${queryString({ scope, pages })}`),
  quoteFunnel: ({ from, to, scope, pages }: ReportRangeParams = {}) =>
    api.get<QuoteFunnelReport>(`/reports/quote-funnel${queryString({ from, to, scope, pages })}`),
  receivables: (scope?: 'all', pages?: string) =>
    api.get<ReceivablesReport>(`/reports/receivables${queryString({ scope, pages })}`),
  crew: ({ from, to, scope, pages }: ReportRangeParams = {}) =>
    api.get<CrewReport>(`/reports/crew${queryString({ from, to, scope, pages })}`),
  capacity: ({ weeks, scope, pages }: { weeks?: number; scope?: 'all'; pages?: string } = {}) =>
    api.get<CapacityReport>(`/reports/capacity${queryString({ weeks, scope, pages })}`),
  leads: ({ from, to, scope, pages }: ReportRangeParams = {}) =>
    api.get<LeadsReport>(`/reports/leads${queryString({ from, to, scope, pages })}`),
  /** Margin per site per month — which standing contracts still pay. */
  jobCosting: ({ from, to, scope, pages }: ReportRangeParams = {}) =>
    api.get<JobCostingReport>(`/reports/job-costing${queryString({ from, to, scope, pages })}`),
  /** What every closed visit made after labor, fees and supplies (0035). */
  jobProfit: ({
    from,
    to,
    scope,
    userId,
    customerId,
    serviceType,
    pages,
  }: ReportRangeParams & { userId?: string; customerId?: string; serviceType?: string } = {}) =>
    api.get<JobProfitReport>(
      `/reports/job-profit${queryString({ from, to, scope, userId, customerId, serviceType, pages })}`,
    ),
  /** Each 1099 contractor's pay for a calendar year, with W-9 status. */
  contractors: ({ year, scope, pages }: { year?: number; scope?: 'all'; pages?: string } = {}) =>
    api.get<ContractorSummary>(`/reports/contractors${queryString({ year, scope, pages })}`),
  /** The accountant export: a row per worker per visit, or a row per worker. */
  accountantExportUrl: (
    file: 'visits' | 'workers',
    params: {
      from?: string;
      to?: string;
      scope?: 'all';
      userId?: string;
      customerId?: string;
      serviceType?: string;
      format?: 'csv' | 'xlsx';
    } = {},
  ) => `${API_BASE}/reports/job-profit/export${queryString({ ...params, file })}`,
  /**
   * The same report as a CSV download. A plain same-origin `<a href>` — the
   * session cookie authenticates it and `content-disposition` names the file,
   * exactly like the company export link in the app shell.
   */
  csvUrl: (
    report:
      | OperationalReportId
      | 'profitability'
      | 'retention'
      | 'quote-funnel'
      | 'receivables'
      | 'crew'
      | 'capacity'
      | 'leads'
      | 'job-costing'
      | 'job-profit'
      | 'contractors',
    params: Record<string, string | number | undefined> = {},
  ) => `${API_BASE}/reports/${report}${queryString({ ...params, format: 'csv' })}`,
};

// --- Logs (logs:read — owner only) -------------------------------------------

/**
 * The one search box (⌘K). One term, every kind of record the caller may
 * read — see routes/search.ts for what is deliberately left out of the
 * response rather than returned empty.
 */
export const searchApi = {
  query: (params: { q: string; limit?: number }) => api.get<SearchResults>(`/search${queryString({ ...params })}`),
};

export const logsApi = {
  activity: (params: { page?: number; pageSize?: number; entityType?: string }) =>
    api.get<Paginated<ActivityEvent>>(`/logs/activity${queryString({ ...params })}`),
  email: (params: { page?: number; pageSize?: number; status?: string }) =>
    api.get<Paginated<EmailLogEntry>>(`/logs/email${queryString({ ...params })}`),
  sms: (params: { page?: number; pageSize?: number; status?: string }) =>
    api.get<Paginated<SmsLogEntry>>(`/logs/sms${queryString({ ...params })}`),
};

// --- API keys (api_keys:manage — owner only) ---------------------------------

export const apiKeysApi = {
  /**
   * The company's keys, the surface a credential may reach, and the scope
   * vocabulary — all three generated server-side so this screen can never
   * promise access the Worker does not allow.
   */
  list: () =>
    api.get<{
      keys: ApiKey[];
      surface: ApiSurfaceEntry[];
      scopes: ApiScopeDescriptor[];
    }>('/api-keys'),
  /** `token` is present exactly once, in this response. */
  create: (body: {
    name: string;
    preset?: 'read' | 'read_write';
    scopes?: ApiScope[];
    expiresInDays?: number | null;
  }) => api.post<{ key: ApiKey; token: string }>('/api-keys', body),
  revoke: (id: string) => api.delete<{ key: ApiKey }>(`/api-keys/${id}`),
  /** Apps connected through OAuth — a different kind of credential, same screen. */
  connectedApps: () => api.get<{ apps: ConnectedApp[] }>('/api-keys/connected-apps'),
  disconnectApp: (id: string) => api.delete<{ app: ConnectedApp }>(`/api-keys/connected-apps/${id}`),
};

// --- AI assistants (Settings → ChatGPT & Claude) ------------------------------

export const assistantConnectionsApi = {
  /** Your own connected assistants — everyone's, for an owner. */
  list: () => api.get<{ connections: AssistantConnection[] }>('/assistant-connections'),
  disconnect: (id: string) => api.delete<{ connection: AssistantConnection }>(`/assistant-connections/${id}`),
};

// --- Connected mailboxes and calendars (your own) -----------------------------

export interface IntegrationCalendar {
  id: string;
  name: string;
  providerCalendarId: string;
  timezone: string | null;
  isPrimary: boolean;
  /** False for a read-only share; the write toggle is not offered for those. */
  providerCanWrite: boolean;
  /** Does it stop the office booking over it? Independent of `displayEnabled`. */
  availabilityEnabled: boolean;
  /** Does it appear in the app's calendar? A hidden one still blocks. */
  displayEnabled: boolean;
  writeAppointmentsEnabled: boolean;
  syncDirection: 'availability_only' | 'read_only' | 'app_to_calendar' | 'two_way';
  privacyMode: 'busy_only' | 'details_for_owner' | 'full_details';
  status: string;
  lastSuccessfulSyncAt: string | null;
  lastError: string | null;
  /** Visits parked awaiting a person, which the sync pass will not pick up. */
  conflictCount: number;
}

export interface IntegrationAccount {
  id: string;
  provider: 'google' | 'microsoft' | 'icloud';
  providerLabel: string;
  email: string;
  displayName: string | null;
  authenticationType: 'oauth' | 'app_password';
  status: string;
  emailCapability: 'unsupported' | 'available' | 'connected';
  calendarCapability: 'unsupported' | 'available' | 'connected';
  emailEnabled: boolean;
  calendarEnabled: boolean;
  lastEmailAt: string | null;
  lastCalendarSyncAt: string | null;
  /** Already in the app's own words — never a provider's raw refusal. */
  lastError: string | null;
  calendars: IntegrationCalendar[];
  createdAt: string;
}

export interface IntegrationsResponse {
  accounts: IntegrationAccount[];
  preferredEmailSender: 'automatic' | 'google' | 'microsoft' | 'icloud' | 'company';
  primaryAccountId: string | null;
  policy: {
    google: boolean;
    microsoft: boolean;
    icloud: boolean;
    employeeEmailSending: boolean;
    externalCalendarsBlockScheduling: boolean;
    twoWayCalendarSync: boolean;
  };
  /** Which providers this deployment can offer at all. */
  available: { google: boolean; microsoft: boolean; icloud: boolean };
}

export const integrationsApi = {
  list: () => api.get<IntegrationsResponse>('/integrations'),
  /**
   * The password is an Apple APP-SPECIFIC password and the field is named that
   * way all the way down, so nothing in the client invites somebody to send an
   * Apple account password instead.
   */
  connectICloudMail: (body: { email: string; appSpecificPassword: string }) =>
    api.post<{
      integrationId: string;
      provider: string;
      email: string;
      mailStatus: string;
    }>('/integrations/icloud/mail', body),
  /**
   * The calendar half, and a separate call on purpose: connecting a calendar
   * and connecting a mailbox are two grants. Same Apple app-specific password,
   * and doing both lands on one account.
   */
  connectICloudCalendar: (body: { email: string; appSpecificPassword: string }) =>
    api.post<{
      integrationId: string;
      provider: string;
      email: string;
      calendarStatus: string;
      calendars: IntegrationCalendar[];
    }>('/integrations/icloud/calendar', body),
  test: (id: string) =>
    api.post<{
      status: 'connected' | 'failed';
      reason?: string;
      /** Which halves were exercised, or which failed. An account can hold both. */
      capabilities?: Array<'email' | 'calendar'>;
      failedCapabilities?: Array<'email' | 'calendar'>;
    }>(`/integrations/${id}/test`, {}),
  update: (id: string, body: { emailEnabled?: boolean; calendarEnabled?: boolean }) =>
    api.patch<IntegrationAccount>(`/integrations/${id}`, body),
  disconnect: (id: string) =>
    api.delete<{
      disconnected: boolean;
      provider: string;
      revokeAtProvider: boolean;
    }>(`/integrations/${id}`),
  setSender: (body: { preference: string; primaryAccountId?: string | null }) =>
    api.patch<{
      preferredEmailSender: string;
      primaryAccountId: string | null;
    }>('/integrations/preferences/email-sender', body),
  updateCalendar: (
    id: string,
    body: {
      availabilityEnabled?: boolean;
      displayEnabled?: boolean;
      writeAppointmentsEnabled?: boolean;
      syncDirection?: string;
      privacyMode?: string;
    },
  ) => api.patch<IntegrationCalendar>(`/integrations/calendars/${id}`, body),
  /** "I have looked at these" — hands the parked links back to the sync pass. */
  retryCalendar: (id: string) => api.post<{ requeued: number }>(`/integrations/calendars/${id}/retry`, {}),
  /**
   * The events to draw beside the company's own visits.
   *
   * `title` is null wherever the calendar's privacy mode does not permit it,
   * and that is the normal case rather than an error: the schedule is entitled
   * to know somebody is busy without being told what they are doing.
   */
  calendarEvents: (params: { startsAt: string; endsAt: string; userIds?: string[] }) =>
    api.get<{ items: ExternalCalendarEvent[] }>(
      `/integrations/calendar-events${queryString({
        startsAt: params.startsAt,
        endsAt: params.endsAt,
        userIds: params.userIds?.length ? params.userIds.join(',') : undefined,
      })}`,
    ),
};

export interface ExternalCalendarEvent {
  id: string;
  userId: string;
  startsAt: string;
  endsAt: string;
  isAllDay: boolean;
  availabilityStatus: string;
  /** Null where the calendar is private. Never a placeholder. */
  title: string | null;
  location: string | null;
  calendarName: string | null;
  /** Set when this event is a visit this app wrote, so it is not drawn twice. */
  appointmentId: string | null;
}

// --- The customer conversation (customers:read) --------------------------------

export interface ConversationMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  channel: 'email' | 'sms' | 'internal' | 'call';
  call?: import('../../shared/voice.ts').InboxCall | null;
  purpose: string;
  subject: string | null;
  body: string | null;
  status: string;
  senderUserId: string | null;
  /** Who wrote it, where a person did. Null for anything a cron composed. */
  senderName: string | null;
  /**
   * Where an answer to this one goes, when that is not simply the address on
   * the customer's record — a visit's site contact, or a customer writing in
   * from somewhere else. Null on everything outbound and on every text.
   */
  replyToAddress: string | null;
  appointmentId: string | null;
  quoteId: string | null;
  invoiceId: string | null;
  createdAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  /** In the app's own words. A provider's raw error never reaches the client. */
  delivery: {
    status: string;
    attempt: number;
    errorCategory: string | null;
    segments: number | null;
  } | null;
  attempts: number;
  /** Files a customer emailed back. Empty for everything this app sent. */
  attachments: MessageAttachment[];
  /**
   * What an assist made of an inbound message: 'confirm' | 'reschedule' |
   * 'cancel' | 'question' | 'other'. Null on everything outbound, and at every
   * company with the assists off. Advisory — nothing branches on it.
   */
  intent: string | null;
  aiSummary: string | null;
}

/**
 * One file on a message.
 *
 * `available` is false for a file that arrived over the size limit: it was
 * recorded rather than dropped, so the timeline can say what came and did not
 * fit instead of showing a message that reads as if nothing was attached.
 */
export interface MessageAttachment {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  available: boolean;
}

export interface ConversationSummary {
  id: string;
  customerId: string | null;
  customerName: string | null;
  status: string;
  assignedUserId: string | null;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  unreadCount: number;
  preview: string | null;
  lastChannel: string | null;
  lastDirection: string | null;
  /** What the last message was about, where an assist has classified it. */
  lastIntent: string | null;
  lastSummary: string | null;
  /** A text from a number nobody recognized, waiting for somebody to resolve it. */
  unresolved: boolean;
  /** The newest message is a voicemail (the inbox shows a voicemail badge while unread). */
  lastVoicemail?: boolean;
}

export const messagesApi = {
  /**
   * Fetch a voicemail's audio again if it never arrived, and transcribe it
   * again. Spends an AI credit, so it asks for the AI notice like a draft.
   */
  retryCall: (callId: string) =>
    api.post<{ call: import('../../shared/voice.ts').InboxCall | null }>(`/messages/calls/${callId}/retry`, {}),
  conversations: (params: {
    page?: number;
    pageSize?: number;
    unread?: boolean;
    customerId?: string;
    unresolved?: boolean;
  }) =>
    api.get<Paginated<ConversationSummary>>(
      `/messages/conversations${queryString({
        page: params.page,
        pageSize: params.pageSize,
        // Asking for one customer's thread directly, rather than paging the
        // inbox looking for it — which silently reports "no messages" for
        // anybody past the first page.
        customerId: params.customerId,
        // The query helper takes scalars; the flag is only sent when it is on,
        // so an absent parameter and "false" mean the same thing at both ends.
        unread: params.unread ? 'true' : undefined,
        // The unknown-senders inbox. Filtered server-side so a stranger's text
        // cannot be pushed off the first page by a busy company's traffic.
        unresolved: params.unresolved ? 'true' : undefined,
      })}`,
    ),
  /**
   * How much is waiting: unread messages, and the threads they arrived on.
   *
   * The badge beside "Messages" reads `messages`. Asked for on its own rather
   * than lifted off a one-row page of the inbox, which is what it used to be —
   * that page's `total` counts conversations, and counted every conversation
   * the company had rather than the unread ones.
   */
  unreadCount: () => api.get<{ conversations: number; messages: number }>('/messages/unread-count'),
  thread: (id: string, params: { page?: number; pageSize?: number } = {}) =>
    api.get<{
      conversation: ConversationSummary;
      messages: ConversationMessage[];
    }>(`/messages/conversations/${id}/messages${queryString({ ...params })}`),
  markRead: (id: string) => api.post<{ unreadCount: number }>(`/messages/conversations/${id}/read`, {}),
  assign: (id: string, userId: string | null) =>
    api.patch<{ assignedUserId: string | null }>(`/messages/conversations/${id}/assignee`, { userId }),
  attach: (id: string, customerId: string) =>
    api.post<{ customerId: string }>(`/messages/conversations/${id}/attach`, {
      customerId,
    }),
  dismiss: (id: string, lastMessageAt: string | null) =>
    api.post<{ dismissed: boolean }>(`/messages/conversations/${id}/dismiss`, { lastMessageAt }),
  addNote: (body: { customerId: string; body: string }, idempotencyKey?: string) =>
    api.post<{ id: string }>('/messages/notes', body, idempotencyKey),
  /**
   * Where the browser fetches an emailed-in file from.
   *
   * A plain same-origin URL rather than a fetch: the Worker serves it with a
   * `Content-Disposition: attachment` header, so the browser's own download
   * does the right thing and the bytes never pass through JavaScript. Session
   * cookies authorize it, the same as every other request here.
   */
  attachmentUrl: (attachmentId: string) => `${API_BASE}/messages/attachments/${attachmentId}`,
  /**
   * A suggested reply for a person to edit. Nothing is sent — the draft lands
   * in the composer and the send is the same button it always was.
   */
  draftReply: (conversationId: string, channel: 'email' | 'sms') =>
    api.post<{ draft: string }>(`/messages/conversations/${conversationId}/draft`, { channel }),
  /**
   * A message somebody typed themselves. Email unless a text is asked for —
   * the channel that costs nothing and needs no consent beyond an address.
   */
  send: (
    body: {
      /** Who it is for, when the caller is looking at a customer. */
      customerId?: string;
      /** The thread it answers, when the caller is looking at the inbox. */
      conversationId?: string;
      channel: 'email' | 'sms';
      subject?: string | null;
      body: string;
      recipientEmail?: string | null;
      recipientPhone?: string | null;
    },
    idempotencyKey?: string,
  ) =>
    api.post<{
      channel: 'email' | 'sms';
      /** The address or number it actually went to. */
      queuedFor: string;
      /** How many segments a text cost. Absent on email, which has none. */
      segments?: number;
      /**
       * True only on a genuine handover to a provider. The request still
       * succeeded — the message is in the timeline either way — so this is
       * the difference between "sent" and "we tried", and the composer says
       * which.
       */
      delivered: boolean;
      /**
       * Neither delivered nor refused: the send's outcome could not be
       * established, or there was no provider configured to establish one.
       *
       * Its own state rather than a failure, because the two call for
       * different actions — check with the customer before re-sending, versus
       * re-send — and the timeline already draws exactly this distinction
       * ("delivery unconfirmed").
       */
      unconfirmed: boolean;
      failureReason?: string | null;
      /** The shared public demo filed it without sending it anywhere. */
      simulated?: boolean;
    }>('/messages/send', body, idempotencyKey),
  consent: (customerId: string) =>
    api.get<{
      events: Array<{
        type: string;
        source: string;
        occurredAt: string;
        phoneNumber: string;
      }>;
    }>(`/messages/consent/${customerId}`),
};

// --- Magic Quote --------------------------------------------------------------

export interface MagicQuoteUploadFields {
  type: 'photo' | 'video' | 'audio' | 'frame';
  /**
   * The client's own id for this capture, minted the instant it was taken.
   *
   * This is the offline contract: a walkthrough happens inside a house with
   * one bar of signal, so the browser keeps retrying an upload until it
   * lands — and cannot tell a request that failed from one that succeeded on
   * a connection that then died. The server treats a repeat of this id within
   * a session as the same capture, so retrying is always safe.
   */
  clientCaptureId: string;
  /** The client's clock, so a capture that queued for ten minutes keeps its place. */
  capturedAt: string;
  durationSeconds?: number;
  /**
   * For a frame or a split-off audio track: the CLIENT id of the video it came
   * from. Not the server's id — at capture time that does not exist yet, and
   * waiting for it would put a network round trip in the middle of recording.
   */
  derivedFromClientCaptureId?: string;
  latitude?: number;
  longitude?: number;
  flagged?: boolean;
}

export const magicQuoteApi = {
  list: (params: { page?: number; pageSize?: number; customerId?: string; propertyId?: string; status?: string }) =>
    api.get<Paginated<MagicQuoteSessionSummary>>(`/magic-quotes${queryString({ ...params })}`),

  start: (body: {
    /** Omitted for a walkthrough that has nobody on file yet. */
    customerId?: string | null;
    propertyId?: string | null;
    mode?: 'quick' | 'guided' | 'free';
    conversationCapture?: boolean;
    /**
     * The device's own id, for a walkthrough that was started with no signal
     * and is being handed over now.
     *
     * `clientCaptureId`'s contract, one level up: sending it again returns the
     * walkthrough it already made rather than starting another. Which is what
     * makes the handover retryable at all — a phone cannot tell a request that
     * never arrived from a reply that never came back, and both are reasons to
     * try again.
     */
    clientSessionId?: string;
  }) =>
    api.post<MagicQuoteSessionSummary>('/magic-quotes', {
      ...body,
      roomCoverageProtocolVersion: 1,
    }),

  /**
   * Gives a walkthrough its customer: an existing one by id, or the details
   * to create one. Exactly one of the two.
   */
  setCustomer: (
    sessionId: string,
    body: {
      customerId?: string;
      customer?: {
        firstName?: string | null;
        lastName?: string | null;
        companyName?: string | null;
        email?: string | null;
        phone?: string | null;
        billingAddressLine1?: string | null;
        billingCity?: string | null;
        billingState?: string | null;
        billingPostalCode?: string | null;
      };
      propertyId?: string | null;
    },
  ) => api.post<{ session: MagicQuoteSessionSummary; customer: Customer }>(`/magic-quotes/${sessionId}/customer`, body),

  get: (id: string, signal?: AbortSignal) =>
    api.get<MagicQuoteSession>(`/magic-quotes/${id}?roomCoverageProtocolVersion=1`, signal),

  confirmCoverage: (sessionId: string, body: { version: number; roomTemplateIds?: string[]; confirmed: true }) =>
    api.post<{ coverage: MagicQuoteCoverage }>(`/magic-quotes/${sessionId}/coverage`, body),

  remove: (id: string) => api.delete<void>(`/magic-quotes/${id}`),

  uploadCapture: (sessionId: string, file: Blob, fields: MagicQuoteUploadFields) => {
    const form = new FormData();
    // A Blob has no name; the server never uses one (keys are built from the
    // tenant id and a fresh ULID), but multipart wants a filename part.
    form.append('file', file, `${fields.clientCaptureId}.bin`);
    form.append('type', fields.type);
    form.append('clientCaptureId', fields.clientCaptureId);
    form.append('capturedAt', fields.capturedAt);
    if (fields.durationSeconds !== undefined) form.append('durationSeconds', String(fields.durationSeconds));
    if (fields.derivedFromClientCaptureId) {
      form.append('derivedFromClientCaptureId', fields.derivedFromClientCaptureId);
    }
    if (fields.latitude !== undefined) form.append('latitude', String(fields.latitude));
    if (fields.longitude !== undefined) form.append('longitude', String(fields.longitude));
    if (fields.flagged) form.append('flagged', 'true');
    return api.upload<MagicQuoteCapture>(`/magic-quotes/${sessionId}/captures`, form);
  },

  addNote: (
    sessionId: string,
    body: {
      type: 'text' | 'room_marker';
      text: string;
      clientCaptureId: string;
      capturedAt: string;
    },
  ) => api.post<MagicQuoteCapture>(`/magic-quotes/${sessionId}/notes`, body),

  flagCapture: (sessionId: string, captureId: string, flagged: boolean) =>
    api.post<{ flagged: boolean }>(`/magic-quotes/${sessionId}/captures/${captureId}/flag`, { flagged }),

  retryCapture: (sessionId: string, captureId: string) =>
    api.post<{ status: string }>(`/magic-quotes/${sessionId}/captures/${captureId}/retry`, {}),

  process: (sessionId: string) =>
    // `reason` is set only when the pass was refused rather than merely
    // unproductive — an exhausted allowance or daily cap, which no amount of
    // poking the queue again will get past.
    api.post<{ processed: number; remaining: number; reason: string | null }>(`/magic-quotes/${sessionId}/process`, {}),

  complete: (sessionId: string) =>
    api.post<{
      ok: boolean;
      reason: string | null;
      readinessScore: number | null;
      pendingCaptureCount: number;
      failedCaptureCount: number;
    }>(`/magic-quotes/${sessionId}/complete`, {}),

  synthesize: (sessionId: string) =>
    api.post<{
      ok: boolean;
      reason: string | null;
      readinessScore: number | null;
    }>(`/magic-quotes/${sessionId}/synthesize`, {}),

  updateRoom: (
    sessionId: string,
    roomId: string,
    body: Partial<{
      name: string;
      roomTemplateId: string | null;
      sizeCode: string | null;
      conditionCode: string | null;
      estimatedMinutes: number | null;
      excluded: boolean;
      notes: string | null;
    }>,
  ) => api.patch<MagicQuoteRoom>(`/magic-quotes/${sessionId}/rooms/${roomId}`, body),

  reviewObservation: (
    sessionId: string,
    observationId: string,
    body: {
      /** 'unreviewed' takes a ruling back — see the review route. */
      reviewStatus?: 'unreviewed' | 'confirmed' | 'corrected' | 'rejected';
      visibility?: 'internal' | 'customer';
      label?: string;
    },
  ) => api.post<MagicQuoteObservation>(`/magic-quotes/${sessionId}/observations/${observationId}/review`, body),

  /** One ruling across everything on screen. */
  reviewObservations: (
    sessionId: string,
    body: {
      observationIds: string[];
      reviewStatus: 'unreviewed' | 'confirmed' | 'rejected';
    },
  ) =>
    api.post<{ changed: number; observations: MagicQuoteObservation[] }>(
      `/magic-quotes/${sessionId}/observations/review`,
      body,
    ),

  addRoom: (
    sessionId: string,
    body: {
      name: string;
      roomTemplateId?: string | null;
      sizeCode?: string | null;
      conditionCode?: string | null;
    },
  ) => api.post<MagicQuoteRoom>(`/magic-quotes/${sessionId}/rooms`, body),

  removeRoom: (sessionId: string, roomId: string) => api.delete<void>(`/magic-quotes/${sessionId}/rooms/${roomId}`),

  /** `allowIncomplete` builds the draft from whatever is ready instead of refusing. */
  generate: (sessionId: string, options: { allowIncomplete?: boolean } = {}) =>
    api.post<{ quoteId: string; skipped: string[] }>(
      `/magic-quotes/${sessionId}/generate`,
      options.allowIncomplete ? { allowIncomplete: true } : {},
    ),

  // POST because it spends an AI action. See docs/ai-credits.md.
  priceAdvice: (sessionId: string) =>
    api.post<{
      advice: { lowCents: number; highCents: number; reasons: string[] } | null;
      engineTotalCents?: number;
      reason: string | null;
    }>(`/magic-quotes/${sessionId}/price-advice`, {}),

  chat: (sessionId: string, message: string) =>
    api.post<{
      reply: string;
      applied: string[];
      rejected: string[];
      citedCaptureIds: string[];
    }>(`/magic-quotes/${sessionId}/chat`, { message }),

  cost: (sessionId: string) => api.get<MagicQuoteCostSummary>(`/magic-quotes/${sessionId}/cost`),
};

// --- Quick Quote --------------------------------------------------------------

export const quickQuoteApi = {
  capabilities: () => api.get<QuickQuoteCapabilities>('/quick-quotes/capabilities'),
  start: (body: QuickQuoteCreateInput) => api.post<QuickQuoteSession>('/quick-quotes', body),
  byRequest: (clientRequestId: string) =>
    api.get<QuickQuoteSession>(`/quick-quotes/by-request/${encodeURIComponent(clientRequestId)}`),
  get: (sessionId: string) => api.get<QuickQuoteSession>(`/quick-quotes/${encodeURIComponent(sessionId)}`),
  answer: (sessionId: string, body: AnswerQuickQuoteInput) =>
    api.post<QuickQuoteSession>(`/quick-quotes/${encodeURIComponent(sessionId)}/answers`, body),
  generate: (sessionId: string, body: { version: number; clientRequestId: string }) =>
    api.post<GenerateQuickQuoteResponse>(`/quick-quotes/${encodeURIComponent(sessionId)}/generate`, body),
  /** Stores one photo for the next Quick Quote and starts its read. A retry with the same id is stored once. */
  uploadPhoto: (photo: Blob, clientPhotoId: string) => {
    const form = new FormData();
    form.append('file', new File([photo], 'photo.jpg', { type: photo.type || 'image/jpeg' }));
    form.append('clientPhotoId', clientPhotoId);
    return api.upload<QuickQuotePhoto>('/quick-quotes/photos', form);
  },
  waitingPhotos: () => api.get<{ photos: QuickQuotePhoto[] }>('/quick-quotes/photos'),
  photo: (photoId: string) => api.get<QuickQuotePhoto>(`/quick-quotes/photos/${encodeURIComponent(photoId)}`),
  /** Reads again a photo skipped for a reason that can clear (credits, today's cap, AI off). */
  readPhotoAgain: (photoId: string) =>
    api.post<QuickQuotePhoto>(`/quick-quotes/photos/${encodeURIComponent(photoId)}/read`),
  removePhoto: (photoId: string) =>
    api.delete<{ deleted: true }>(`/quick-quotes/photos/${encodeURIComponent(photoId)}`),
};

// ---------------------------------------------------------------------------
// Security (two-factor authentication)
// ---------------------------------------------------------------------------

export interface MfaMethodSummary {
  id: string;
  type: 'totp' | 'sms' | 'webauthn';
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  maskedPhone?: string;
  backedUp?: boolean;
}

export interface SecurityStatus {
  enabled: boolean;
  required: boolean;
  methods: MfaMethodSummary[];
  availableMethods: ('totp' | 'sms' | 'webauthn' | 'recovery')[];
  recoveryCodesRemaining: number;
  limits: { totp: number; sms: number; webauthn: number };
  freshAuth: { fresh: boolean; expiresAt: string | null };
  trustedDeviceDays: number;
}

export interface TrustedDevice {
  id: string;
  label: string | null;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  current: boolean;
}

export interface SecuritySession {
  id: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  mfaVerifiedAt: string | null;
  impersonatedByAdminEmail: string | null;
  current: boolean;
}

export interface ChallengeOption {
  methodId: string | null;
  type: 'totp' | 'sms' | 'webauthn' | 'recovery';
  label: string;
}

/**
 * Settings → Security.
 *
 * The one thing worth noticing in this client: an enrollment secret and a set
 * of recovery codes appear in exactly one response each and are never fetched
 * again — so the screens that show them hold them in component state and say
 * so, rather than assuming they can re-read them later.
 */
export const securityApi = {
  status: () => api.get<SecurityStatus>('/security'),
  enrollTotp: (label?: string) =>
    api.post<{ methodId: string; secret: string; uri: string }>('/security/totp/enroll', { label }),
  verifyTotp: (methodId: string, code: string) =>
    api.post<{ recoveryCodes: string[] | null }>('/security/totp/verify', { methodId, code }),
  enrollSms: (phone: string, label?: string) =>
    api.post<{ methodId: string; challengeId: string; maskedPhone: string }>('/security/sms/enroll', { phone, label }),
  verifySms: (challengeId: string, code: string) =>
    api.post<{ recoveryCodes: string[] | null }>('/security/sms/verify', { challengeId, code }),
  securityKeyOptions: () =>
    api.post<{ challengeId: string; publicKey: ServerPublicKeyCreation }>(
      '/security/security-keys/register/options',
      {},
    ),
  registerSecurityKey: (body: {
    challengeId: string;
    label: string;
    clientDataJson: string;
    attestationObject: string;
    transports?: string[];
  }) => api.post<{ methodId: string; recoveryCodes: string[] | null }>('/security/security-keys/register', body),
  rename: (id: string, label: string) => api.patch<{ renamed: boolean }>(`/security/methods/${id}`, { label }),
  remove: (id: string) => api.delete<void>(`/security/methods/${id}`),
  regenerateRecoveryCodes: () => api.post<{ codes: string[] }>('/security/recovery-codes/regenerate', {}),
  elevateOptions: () => api.get<{ options: ChallengeOption[] }>('/security/elevate/options'),
  elevateChallenge: (body: { type: string; methodId?: string | null }) =>
    api.post<{ challengeId: string; type: string; maskedPhone?: string; publicKey?: ServerPublicKeyRequest }>(
      '/security/elevate/challenge',
      body,
    ),
  elevateVerify: (body: { challengeId: string; code?: string; assertion?: unknown }) =>
    api.post<{ verified: boolean; fresh: boolean; expiresAt: string | null }>('/security/elevate/verify', body),
  trustedDevices: () => api.get<{ devices: TrustedDevice[] }>('/security/trusted-devices'),
  revokeTrustedDevice: (id: string) => api.delete<void>(`/security/trusted-devices/${id}`),
  clearTrustedDevices: () => api.delete<void>('/security/trusted-devices'),
  sessions: () => api.get<{ sessions: SecuritySession[] }>('/security/sessions'),
  revokeSession: (id: string) => api.delete<void>(`/security/sessions/${id}`),
  revokeOtherSessions: () => api.post<{ revoked: boolean }>('/security/sessions/revoke-others', {}),
  // Signing the mobile app in with a QR code (components/phone-sign-in.tsx).
  startQrSignIn: (password?: string) =>
    api.post<{ id: string; payload: string; expiresAt: string; expiresInSeconds: number }>(
      '/security/qr-sign-in',
      password === undefined ? {} : { password },
    ),
  qrSignIn: (id: string) => api.get<QrSignInView>(`/security/qr-sign-in/${id}`),
  approveQrSignIn: (id: string, code: string) => api.post<QrSignInView>(`/security/qr-sign-in/${id}/approve`, { code }),
  cancelQrSignIn: (id: string) => api.delete<void>(`/security/qr-sign-in/${id}`),
};

/** The website's view of one QR sign-in attempt. */
export interface QrSignInView {
  id: string;
  status: QrSignInStatus;
  expiresAt: string;
  /** Time left when this answer was sent. Count down from receipt, not from `expiresAt`. */
  expiresInSeconds: number;
  platform: QrSignInPlatform | null;
  attemptsRemaining: number;
}

// ---------------------------------------------------------------------------
// Refer & Earn
// ---------------------------------------------------------------------------

export interface ReferralOverview {
  enabled: boolean;
  // Null, not absent, for a company whose owner has not opened the screen yet:
  // `GET /referrals/me` creates the code only for a caller who could rename it,
  // because the code carries the company's public display name and the slug in
  // its public link. The screen renders the program, the balance and the
  // history without them.
  code?: string | null;
  url?: string | null;
  displayName?: string | null;
  referrerRewardMonths?: number;
  referredTrialDays?: number;
  stats?: { total: number; pending: number; qualified: number };
  credits?: { earnedMonths: number; usedMonths: number; availableMonths: number };
}

export interface ReferralHistoryEntry {
  id: string;
  status: string;
  signedUpAt: string | null;
  qualifiedAt: string | null;
  rewardedAt: string | null;
  createdAt: string;
}

export const referralsApi = {
  overview: () => api.get<ReferralOverview>('/referrals/me'),
  setDisplayName: (displayName: string) => api.patch<{ displayName: string }>('/referrals/me', { displayName }),
  history: () =>
    api.get<{
      referrals: ReferralHistoryEntry[];
      invitations: { id: string; recipientEmail: string; sentAt: string; deliveryStatus: string | null }[];
    }>('/referrals/history'),
  credits: () =>
    api.get<{
      summary: { earnedMonths: number; usedMonths: number; availableMonths: number };
      entries: {
        id: string;
        type: string;
        months: number;
        amountCents: number | null;
        reason: string | null;
        createdAt: string;
      }[];
    }>('/referrals/credits'),
  invite: (body: { recipientEmail: string; subject?: string; body?: string }) =>
    api.post<{ sent: boolean; deliveryStatus: string }>('/referrals/email', body),
};

export const savedTeamsApi = {
  list: () => api.get<{ items: SavedTeam[] }>('/teams'),
  create: (body: Record<string, unknown>) => api.post<SavedTeam>('/teams', body),
  update: (id: string, body: Record<string, unknown>) =>
    withAvailabilityConfirmation((token) =>
      api.patch<{ team: SavedTeam; updatedVisits: number; updatedSeries: number; preservedVisits: number }>(
        `/teams/${id}`,
        { ...body, ...(token ? { availabilityConfirmation: token } : {}) },
      ),
    ),
};

export const checklistStackApi = {
  templates: () => api.get<{ templates: ChecklistTemplate[] }>('/checklist-templates'),
  saveTemplate: (id: string | null, body: { requestId: string; version?: number; definition: ChecklistDefinition }) =>
    id
      ? api.put<{ template: ChecklistTemplate; refreshed: number }>(`/checklist-templates/${id}`, body)
      : api.post<{ template: ChecklistTemplate; refreshed: number }>('/checklist-templates', body),
  /** Refused (409 CHECKLIST_TEMPLATE_IN_USE) while a property or a schedule weekday uses it. */
  deleteTemplate: (id: string) => api.delete<{ deleted: true }>(`/checklist-templates/${id}`),
  stack: (propertyId: string) => api.get<{ stack: ChecklistStack }>(`/properties/${propertyId}/checklist/stack`),
  saveStack: (propertyId: string, body: { requestId: string; version: number; layers: ChecklistLayer[] }) =>
    api.put<{ stack: ChecklistStack; refreshed: number }>(`/properties/${propertyId}/checklist/stack`, body),
  preview: (
    propertyId: string,
    date: string,
    visitType: ChecklistVisitType | null,
    ordinal: number,
    seriesId?: string,
  ) =>
    api.get<{
      tasks: {
        sourceKey: string;
        sectionKey?: string | null;
        layerName?: string | null;
        areaName: string;
        areaNameI18n: string | null;
        areaNameTranslations: LocalizedText | null;
        taskName: string;
        taskNameI18n: string | null;
        taskNameTranslations: LocalizedText | null;
        due: boolean;
        estimatedMinutes?: number;
      }[];
    }>(
      `/properties/${propertyId}/checklist/stack/preview?${new URLSearchParams({ date, ordinal: String(ordinal), ...(visitType ? { visitType } : {}), ...(seriesId ? { seriesId } : {}) })}`,
    ),
  prepare: (appointmentId: string) =>
    api.post<VisitChecklist>(`/schedule/appointments/${appointmentId}/checklist/prepare`, {}),
  editVisit: (
    appointmentId: string,
    body: {
      requestId: string;
      revision: number;
      scope: 'this_visit' | 'this_and_upcoming';
      expectedStartsAt: string;
      expectedOccurrenceDate: string;
      visitType: ChecklistVisitType | null;
      definition: ChecklistDefinition;
    },
  ) =>
    api.put<{ revision: number; changed: number; preserved: number }>(
      `/schedule/appointments/${appointmentId}/checklist/definition`,
      body,
    ),
};

export interface InboxActionContext {
  sourceMessageId: string | null;
  visits: Array<{
    id: string;
    scheduled_date: string;
    start_time_local: string;
    duration_minutes: number;
    timezone: string;
  }>;
  /** The visit the customer's message points at, to preselect. */
  suggestedVisitId?: string | null;
  suggestions: InboxActionSuggestion[];
}
export const inboxActionsApi = {
  context: (conversationId: string) => api.get<InboxActionContext>(`/inbox-actions/conversations/${conversationId}`),
  suggest: (conversationId: string, input: { id: string; appointmentId: string; sourceMessageId: string }) =>
    api.post<{ suggestion: InboxActionSuggestion | null; confirmPath?: string }>(
      `/inbox-actions/conversations/${conversationId}/suggest`,
      input,
    ),
  confirm: (id: string, token: string) =>
    api.get<{ suggestion: InboxActionSuggestion }>(`/inbox-actions/${id}/confirm?token=${encodeURIComponent(token)}`),
  decide: (id: string, decision: 'approve' | 'dismiss', token?: string) =>
    api.post<{ status: 'approved' | 'dismissed'; alreadyDecided?: boolean }>(`/inbox-actions/${id}/decision`, {
      decision,
      ...(token ? { token } : {}),
    }),
};
